import {useEffect, useMemo, useRef, useState, useCallback} from "react";

import {AgGridReact} from "ag-grid-react";
import {Backdrop, Box, Checkbox, CircularProgress, FormControlLabel, IconButton, Tooltip} from "@mui/material"
import {blue, lightBlue, red} from '@mui/material/colors';
import {FilterAlt, EditNote, Transform, ViewComfySharp, SwapVert} from "@mui/icons-material";
import moment from 'moment';

import {withTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {cloneDeep, find, findIndex, includes, isEmpty, isEqual, isNil, isNull, isUndefined, keys, size, sortBy, values} from "lodash";

import psiSimulationService from "@plannel/services/rp/psi-simulation-service"
import planVersionService from "@plannel/services/data/plan-version-service";
import runRpService from "@plannel/services/rp/run-rp-service";
import cycleCalendarService from "@plannel/services/data/cycle-calendar-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";

import SplitterContainer from "@plannel/components/SplitterContainer";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import CalendarModal from "@plannel/components/modal/CalendarModal"
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader"
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import {CheckButton, DeleteButton, EditButton, SaveButton, SettingsButton, FilterButton} from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip, { AbcXyzChipInline } from "@plannel/components/aggrid/AbcXyzChip";
import RpUtil from "../utils/rp-util";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import RpVersionFilter from "@plannel/components/filter/RpVersionFilter";
import LocationTreeFilter from "@plannel/components/filter/LocationTreeFilter";
import {RPFilterModal} from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import LayoutListModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/LayoutListModal";
import p13nService from "@plannel/services/system/p13n-service";
import InvCovDaysSettings from "@plannel/components/modal/InvCovDaysSettings";

import NotesModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/NotesModal";
import RunModal from "@plannel/components/modal/RunModal";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import RpPsiSimulationEditModal from "@plannel/pages/replenishment-plan/rp-modal/RpPsiSimulationEditModal";
import ZeroExclusionFilter from "@plannel/components/filter/ZeroExclusionFilter";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import DimensAttrModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DimensAttrModal";
import companyService from "@plannel/services/data/company-service";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import { FS } from "@plannel/utils/font-util";

const { rpMeasure, versionStatusCode, muicolor, filterType, item, buckets: { DAY, WEEK, MONTH }, inventoryCoverageDaysPolicy, dpABCConfig } = CodeUtil;
const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = CodeUtil.rpPolicyCd;
const RP_POLICY_LIST = [P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM];
const { SIMULATION, PLANNED } = versionStatusCode;
const { DEMAND_QTY, KIT_FCST_DEMAND_QTY, FCST_DEMAND_QTY,INTERNAL_DEMAND_QTY, DOWNSTREAM_DEMAND_QTY_SUM, DOWNSTREAM_EOH_QTY_SUM, RP_ORDER, ARRIVAL_QTY, ARRIVAL_QTY_PLAN, ARRIVAL_QTY_CFM, SHIPMENT_QTY, SHIPMENT_QTY_PLAN, SHIPMENT_QTY_CFM, SHIPMENT_QTY_FCST, SHIPMENT_QTY_REPL, BOH, EOH, TARGET_STOCK, ROP, SIM_ARRIVAL, SIM_BOH, SIM_EOH, SIM_RP_ORDER, SIM_SHIPMENT_QTY, SIM_DEMAND_QTY, LAST_RELEASED_REPLENISHMENT_QTY, LAST_RELEASED_PURCHASE_QTY, BACKORDER_QTY, SIM_BACKORDER_QTY, INV_COV_DAYS, SIM_INV_COV_DAYS, SHIPMENT_SHORTAGE_QTY } = rpMeasure;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const { snackBarMessageColor } = ColorUtil;
const { INV_COV_DAYS_THRESHOLD, INV_COV_DAYS_APPLY, INV_COV_DAYS_COMPUTED_METHOD } = inventoryCoverageDaysPolicy;
const PSI_SIMULATION = "PSI_SIMULATION";
const bucketDaysObj = {
  day: 1,
  week: 7,
  month: 30,
};
const gridId = "grid";
const INV_CLASS_CD = "invClassCd";

// 노트 마커를 그릴 컬럼 우선순위 — 앞 컬럼이 숨겨지면 다음 컬럼이 대신 표시한다
const NOTE_MARKER_COLUMNS = [["itemNmGrp", "itemCdGrp"], ["destLocNmGrp"]];
const NOTE_MARKER_GROUP_FIELD = { itemNmGrp: "itemNm", itemCdGrp: "itemCd", destLocNmGrp: "destLocNm" };
const NOTE_MARKER_REFRESH_COLS = ["itemNmGrp", "itemCdGrp", "destLocNmGrp"];

const PsiSimulation = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [10, 30, 50, 100];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [rpVersionFilter, setRpVersionFilter] = useState(reduxViewState?.rpVersion ? reduxViewState.rpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { originLocation: null, destLocation: null, category: null });

  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [defaultValue, setDefaultValue] = useState([]);
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [appliedExceptions, setAppliedExceptions] = useState(reduxViewState?.appliedExceptions ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(
    reduxViewState?.advancedFilters?.children?.length > 0 ? reduxViewState.advancedFilters : null
  );
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(null);
  const [isAFActive, setIsAFActive] = useState(!!(reduxViewState?.advancedFilters?.children?.length > 0));
  const [openFilter, setOpenFilter] = useState(false);

  const [filterData, setFilterData] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [runModalOpen, setRunModalOpen] = useState(false);
  // item attribute
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  const [dimensionAttrData, setDimensionAttrData] = useState(reduxViewState?.dimensionAttrData ?? {});
  const [attrConfig, setAttrConfig] = useState(null);

  const [selectItemHrchy, setSelectItemHrchy] = useState([]);
  const [isRun, setIsRun] = useState(false);

  const [sortParams, setSortParams] = useState({});
  const [backDropOpen, setBackDropOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [simulationData, setSimulationData] = useState([]);
  const [simulationHeaders, setSimulationHeaders] = useState([]);
  const gridHeaderMap = useRef({});
  const chartSelectedRowsRef = useRef([]); // 마지막 차트 선택 행 — 언어 변경 시 재조립용
  const localDateHeaderMap = useRef({});
  const noteKeySet = useRef(new Set());
  const [simulationUpdateDatas, setSimulationUpdateDatas] = useState([]);
  const [onlyExceptionApplied, setOnlyExceptionApplied] = useState(reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "onlyExceptionApplied") ? reduxViewState.onlyExceptionApplied : false);

  const gridRef = useRef();
  const bucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);
  const colsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "colsHide") ? reduxViewState.colsHide : true;

  const [columnApi, setColumnApi] = useState(null);
  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [p13Measures, setP13Measures] = useState([]);
  const [p13nColumnState, setP13nColumnState] = useState([]);
  const allMeasure = CodeUtil.psiSimulationMeasures;
  const defaultMeasure = CodeUtil.psiSimulationDefaultMeasures;
  const [displayName, setDisplayName] = useState(reduxViewState?.displayName ?? null);

  const rowsPerCombination = useMemo(() => {
    return measures?.length === 0 ? allMeasure?.length : measures?.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measures]);
  const [itemCustomValue, setItemCustomValue] = useState(
    Array.isArray(reduxViewState?.filter?.category) && reduxViewState.filter.category.length > 0
      ? reduxViewState.filter.category
      : null
  );
  const [treeLoad, setTreeLoad] = useState(false);
  const [layoutModalData, setLayoutModalData] = useState(reduxViewState?.layoutModalData ?? {});
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [initLoad, setInitLoad] = useState(false);
  const layoutLoad = useRef(false);
  const [noteUpdateFlg, setNoteUpdateFlg] = useState(false);
  const [updateNoteData, setUpdateNoteData] = useState({});
  const [noteOpen, setNoteOpen] = useState(true);
  const [noteCellData, setNoteCellData] = useState({});
  const [selectedRow, setSelectedRow] = useState([]);

  const [initialFlg, setInitialFlg] = useState(reduxViewState?.initialFlg ?? false);
  const [orderByItem, setOrderByItem] = useState(reduxViewState?.orderByItem ?? null);

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [cycleCalendar, setCycleCalendar] = useState({});

  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);

  const [editTarget, setEditTarget] = useState(reduxViewState?.editTarget ?? SIM_RP_ORDER);
  const editTargetRef = useRef(reduxViewState?.editTarget ?? SIM_RP_ORDER);
  const defaultTypeOptions = { strokeWidth: 1.5, barSize: 20 };

  const getDisplayText = (translationKey, originTxt) => {
    if (displayName != null && displayName[translationKey]) {
      return displayName[translationKey];
    }
    return t(originTxt);
  };

  const chartSeries = [
    { field: DEMAND_QTY, text: getDisplayText(DEMAND_QTY, DEMAND_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: KIT_FCST_DEMAND_QTY, text: getDisplayText(KIT_FCST_DEMAND_QTY, "kitFcstDemandQty"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: FCST_DEMAND_QTY, text: getDisplayText(FCST_DEMAND_QTY, "fcstDemandQty"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: INTERNAL_DEMAND_QTY, text: getDisplayText(INTERNAL_DEMAND_QTY, "internalDemandQty"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: DOWNSTREAM_DEMAND_QTY_SUM, text: getDisplayText(DOWNSTREAM_DEMAND_QTY_SUM, "downstreamDemandQtySum"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: DOWNSTREAM_EOH_QTY_SUM, text: getDisplayText(DOWNSTREAM_EOH_QTY_SUM, "downstreamEohQtySum"), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: BACKORDER_QTY, text: getDisplayText(BACKORDER_QTY, BACKORDER_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SIM_BACKORDER_QTY, text: getDisplayText(SIM_BACKORDER_QTY, SIM_BACKORDER_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: RP_ORDER, text: getDisplayText(RP_ORDER, RP_ORDER), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: ARRIVAL_QTY, text: getDisplayText(ARRIVAL_QTY, ARRIVAL_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: ARRIVAL_QTY_PLAN, text: getDisplayText(ARRIVAL_QTY_PLAN, ARRIVAL_QTY_PLAN), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: ARRIVAL_QTY_CFM, text: getDisplayText(ARRIVAL_QTY_CFM, ARRIVAL_QTY_CFM), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_QTY, text: getDisplayText(SHIPMENT_QTY, SHIPMENT_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_QTY_PLAN, text: getDisplayText(SHIPMENT_QTY_PLAN, SHIPMENT_QTY_PLAN), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_QTY_CFM, text: getDisplayText(SHIPMENT_QTY_CFM, SHIPMENT_QTY_CFM), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: BOH, text: getDisplayText(BOH, BOH), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: EOH, text: getDisplayText(EOH, EOH), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("qty") },
    { field: TARGET_STOCK, text: getDisplayText(TARGET_STOCK, TARGET_STOCK), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("qty") },
    { field: ROP, text: getDisplayText(ROP, ROP), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("qty") },
    { field: SIM_DEMAND_QTY, text: getDisplayText(SIM_DEMAND_QTY, SIM_DEMAND_QTY), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SIM_RP_ORDER, text: getDisplayText(SIM_RP_ORDER, SIM_RP_ORDER), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: LAST_RELEASED_REPLENISHMENT_QTY, text: getDisplayText(LAST_RELEASED_REPLENISHMENT_QTY, LAST_RELEASED_REPLENISHMENT_QTY), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: LAST_RELEASED_PURCHASE_QTY, text: getDisplayText(LAST_RELEASED_PURCHASE_QTY, LAST_RELEASED_PURCHASE_QTY), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SIM_ARRIVAL, text: getDisplayText(SIM_ARRIVAL, SIM_ARRIVAL), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SIM_SHIPMENT_QTY, text: getDisplayText(SIM_SHIPMENT_QTY, SIM_SHIPMENT_QTY), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SIM_BOH, text: getDisplayText(SIM_BOH, SIM_BOH), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("qty") },
    { field: SIM_EOH, text: getDisplayText(SIM_EOH, SIM_EOH), type: "bar", typeOptions: { ...defaultTypeOptions }, orientation: "right", groupName: t("qty") },
    { field: INV_COV_DAYS, text: getDisplayText(INV_COV_DAYS, INV_COV_DAYS), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SIM_INV_COV_DAYS, text: getDisplayText(SIM_INV_COV_DAYS, SIM_INV_COV_DAYS), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") },
    { field: SHIPMENT_SHORTAGE_QTY, text: getDisplayText(SHIPMENT_SHORTAGE_QTY, SHIPMENT_SHORTAGE_QTY), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") }
  ];

  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [psiEditModalOpen, setPsiEditModalOpen] = useState(false);
  const [invCovDaysConfig, setInvCovDaysConfig] = useState({});
  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel,
    xAxisSortMap: gridHeaderMap.current
  };

  // useShipmentTxn 설정에 따라 동적으로 requiredMeasure 생성
  const requiredMeasure = useMemo(() => {
    const baseMeasures = [ARRIVAL_QTY_CFM, SIM_RP_ORDER, SIM_ARRIVAL, SIM_SHIPMENT_QTY, SIM_BOH, SIM_EOH, SIM_DEMAND_QTY, SIM_BACKORDER_QTY];
    // useShipmentTxn이 활성화된 버전이면 SHIPMENT_QTY_CFM 추가
    if (rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN) {

      return [...baseMeasures, SHIPMENT_QTY_CFM];
    }


    return baseMeasures;
  }, [rpVersionFilter]);

  const cellStyleByMeasure = params => {
    if (params?.data?.originMeasure) {
      const { originMeasure } = params.data;
      if (editTargetRef.current === SIM_RP_ORDER) {
        if (isEqual(originMeasure, SIM_RP_ORDER) || isEqual(originMeasure, SIM_DEMAND_QTY)) {
          return { background: lightBlue[50] };
        }
      } else {
        if (isEqual(originMeasure, SIM_ARRIVAL) || isEqual(originMeasure, SIM_DEMAND_QTY)) {
          return { background: lightBlue[50] };
        }
      }
    }

    return {};
  }

  const findMeasureData = (nodes, measure) => {
    for (const node of nodes) {
      if (isEqual(node.data.originMeasure, measure)) {
        return node?.data;
      }
    }

    return {};
  }

  const handleDimenClose = () => {
    setDimenModalOpen(false);
  };

  const getInvCovDays = (startIdx, headers, resultsData, isSimulation) => {
    const bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    const bucketDays = bucketDaysObj[bucketType] || 1;

    const eohQtyPivotData = isSimulation ? "eohQtyAdjPivotData" : "eohQtyPivotData";
    const demandQtyPivotData = isSimulation ? "demandQtyAdjPivotData" : "demandQtyPivotData";

    let invCovDays = 0;
    let eoh = resultsData[eohQtyPivotData][startIdx];
    const endIdx = headers.length;

    for (let i = startIdx + 1; i < endIdx; i++) {
      const demandQty = resultsData[demandQtyPivotData][i];
      const leftStock = eoh - demandQty;

      if (leftStock >= 0) {
        eoh = leftStock;
        invCovDays += bucketDays;
      } else {
        const demandPerDay = Math.max(demandQty / bucketDays, 1);
        invCovDays += Math.floor(eoh / demandPerDay);
        break;
      }
    }

    return invCovDays;
  }

  const getSimInvCovDays = (startIdx, headers, simEohData, simDemandData) => {
    const bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    const bucketDays = bucketDaysObj[bucketType] || 1;

    let invCovDays = 0;
    let eoh = simEohData[headers[startIdx]];
    const endIdx = headers.length;

    for (let i = startIdx + 1; i < endIdx; i++) {
      const demandQty = simDemandData[headers[i]];
      const leftStock = eoh - demandQty;

      if (leftStock >= 0) {
        eoh = leftStock;
        invCovDays += bucketDays;
      } else {
        const demandPerDay = Math.max(demandQty / bucketDays, 1);
        invCovDays += Math.floor(eoh / demandPerDay);
        break;
      }
    }

    return invCovDays;
  }

  const getShipmentShortageQty = (startIdx, headers, resultsData) => {
    const bohQtyPivotData = "bohQtyPivotData";
    const arrivalQtyCfmPivotData = "arrivalQtyCfmPivotData";
    const salesQtyPlanPivotData = "salesQtyPlanPivotData";

    const bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    const planExecDate = rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE;
    const planExecBucket = RpUtil.getBucketDate(bucketType, planExecDate);

    if (!planExecBucket || headers.indexOf(planExecBucket) !== startIdx) {
      return 0;
    }

    const boh = resultsData[bohQtyPivotData][startIdx] ?? 0;
    const arrivalQtyCfm = resultsData[arrivalQtyCfmPivotData][startIdx] ?? 0;
    const salesQtyPlan = resultsData[salesQtyPlanPivotData][startIdx] ?? 0;

    return Math.max(0, salesQtyPlan - (boh + arrivalQtyCfm));
  }

  const validateCautionTarget = (invCovDays) => {
    if (!isEmpty(invCovDaysConfig) && invCovDaysConfig[INV_COV_DAYS_APPLY]) {
      if (invCovDaysConfig[INV_COV_DAYS_COMPUTED_METHOD] === "lessThan") {
        return invCovDays < invCovDaysConfig[INV_COV_DAYS_THRESHOLD];
      } else {
        return invCovDays > invCovDaysConfig[INV_COV_DAYS_THRESHOLD];
      }
    }

    return false;
  }

  const dataCellStyle = (params, headers) => {
    const cellStyle = cellStyleByMeasure(params);
    const { column, data, node, value } = params;
    if (data && column) {
      const { originMeasure } = data;
      if (originMeasure && column?.colId) {
        const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
        const replaceColId = column?.colId.replace(replacePostFix, "");
        const headerIndex = headers.indexOf(replaceColId);
        if (headerIndex !== -1 && value !== null) {
          const locationItemNodes = node.parent.allLeafChildren;
          const numValue = Number(value);

          const rpOrderQty = node.data.rpOrderQtyPivotData[headerIndex];
          const arrivalQty = node.data.arrivalQtyPivotData[headerIndex];
          const bohQty = node.data.bohQtyPivotData[headerIndex];
          const eohQty = node.data.eohQtyPivotData[headerIndex];
          const demandQty = node.data.demandQtyPivotData[headerIndex];
          const shipmentQty = node.data.salesQtyPivotData[headerIndex];
          const simDemnadData = findMeasureData(locationItemNodes, SIM_DEMAND_QTY);
          const simDemandQty = simDemnadData[headers[headerIndex]];
          const backorderQty = node.data.backorderQtyPivotData[headerIndex];
          const invCovDays = getInvCovDays(headerIndex, headers, node.data, false);

          const isSimDemandModifed = isEqual(originMeasure, SIM_DEMAND_QTY) && !isEqual(numValue, Number(demandQty));
          const isSimBackorderModified = isEqual(originMeasure, SIM_BACKORDER_QTY) && !isEqual(numValue, Number(backorderQty));
          const isSimModified = isEqual(originMeasure, SIM_RP_ORDER) && !isEqual(numValue, Number(rpOrderQty));
          const isSimArrivalModified = isEqual(originMeasure, SIM_ARRIVAL) && !isEqual(numValue, Number(arrivalQty));
          const isSimBohModified = isEqual(originMeasure, SIM_BOH) && !isEqual(numValue, Number(bohQty));
          const isSimEohModified = isEqual(originMeasure, SIM_EOH) && !isEqual(numValue, Number(eohQty));
          const isSimSalesModified = isEqual(originMeasure, SIM_SHIPMENT_QTY) && !isEqual(numValue, Number(shipmentQty));
          const isException = isEqual(originMeasure, SIM_SHIPMENT_QTY) && simDemandQty > numValue;
          const isSimInvCovDaysModified = isEqual(originMeasure, SIM_INV_COV_DAYS) && !isEqual(numValue, Number(invCovDays));

          if (isSimDemandModifed || isSimModified || isSimArrivalModified || isSimBohModified || isSimEohModified || isSimSalesModified || isSimBackorderModified || isSimInvCovDaysModified) {
            cellStyle.color = blue.A700;
            cellStyle.fontWeight = 'bold';
          }
          if (isException) {
            cellStyle.background = red[100];
            cellStyle.fontStyle = 'italic';
          }

          if (noteKeySet.current.size) {
            const noteKey = CodeUtil.getNoteKey(viewName, {
              originLocCd: data.originLocCd, destLocCd: data.destLocCd, itemCd: data.itemCd,
              baseDate: replaceColId, originMeasure: data.originMeasure, measure: data.measure
            });
            if (noteKeySet.current.has(noteKey)) {
              cellStyle.borderLeft = "3px solid #4caf50";
            }
          }

          if (data?.originMeasure && (isEqual(data.originMeasure, INV_COV_DAYS) || isEqual(data.originMeasure, SIM_INV_COV_DAYS)) && validateCautionTarget(value)) {
            cellStyle.color = red[500];
          }

          const useShipmentTxn = rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN;

          if (useShipmentTxn) {
            const planExecDate = rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE ?? moment().format('YYYY-MM-DD');
            const baseDate = replaceColId;

            if (planExecDate && baseDate) {
              const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT || DAY;
              const bucket = moment(baseDate);
              const execDate = moment(planExecDate);
              if (bucket.isSameOrBefore(execDate, bucketUnit)) {
                cellStyle.background = null;
              }
            }

            if (isEqual(originMeasure, SIM_ARRIVAL)) {
              const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT || DAY;
              const bucket = moment(baseDate);
              const execDate = moment(planExecDate);

              const leadTime = params.data?.otdLt || 0;
              const ltBucketCount = RpUtil.getOtdLtByBucket(bucketUnit, leadTime) || 0;
              const freezeDate = execDate.clone().add(ltBucketCount, bucketUnit);

              if (bucket.isSameOrBefore(freezeDate, bucketUnit)) {
                cellStyle.background = null;
              }
            }
          }
        }
      }
    }
    return cellStyle;
  }

  // 같은 도착지/품목 중 현재 보이는 첫 컬럼만 노트 마커를 담당한다
  const getNoteMarkerColId = (api, colId) => {
    const markerColumns = NOTE_MARKER_COLUMNS.find(columns => columns.includes(colId));
    if (!markerColumns) {
      return null;
    }
    return markerColumns.find(column => api?.getColumn(column)?.isVisible()) ?? null;
  }

  // 그룹 값이 실제로 표시되는 행 = 해당 그룹 노드의 leaf 중 표시 순서상 첫 행
  const isFirstRowOfGroup = (node, groupField) => {
    let group = node?.parent;
    while (group && group.field !== groupField) group = group.parent;

    const leaves = group?.allLeafChildren;
    if (isEmpty(leaves)) {
      return false;
    }

    let firstIndex = Number.MAX_SAFE_INTEGER;
    for (const leaf of leaves) {
      if (!isNull(leaf.rowIndex) && leaf.rowIndex < firstIndex) firstIndex = leaf.rowIndex;
    }
    return node.rowIndex === firstIndex;
  }

  const locItemNoteCellStyle = (params) => {
    const { data, node, api, column } = params;
    if (!data || !noteKeySet.current.size) {
      return {};
    }

    const colId = column?.getColId();
    if (getNoteMarkerColId(api, colId) !== colId) {
      return {};
    }

    const noteKey = CodeUtil.getNoteKey(viewName, { originLocCd: data.originLocCd, destLocCd: data.destLocCd, itemCd: data.itemCd });
    if (!noteKeySet.current.has(noteKey)) {
      return {};
    }

    return isFirstRowOfGroup(node, NOTE_MARKER_GROUP_FIELD[colId]) ? { borderLeft: "3px solid #4caf50" } : {};
  }

  const isDateInBucket = (bucketDate, targetDate, bucketType) => {
    const bucket = moment(bucketDate);
    const target = moment(targetDate);

    switch (bucketType) {
      case DAY:
        return bucket.isSame(target, DAY);

      case WEEK:
        return bucket.isSame(target, WEEK);

      case MONTH:
        return bucket.isSame(target, MONTH);

      default:
        return false;
    }
  }

  const dynamicCellEditable = params => {
    const { measure, baseDate } = params.data;
    const useShipmentTxn = rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN;

    if (useShipmentTxn) {
      const planExecDate = rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE || moment().format('YYYY-MM-DD');

      if (planExecDate && baseDate) {
        const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT || DAY;
        const bucket = moment(baseDate);
        const execDate = moment(planExecDate);
        if (bucket.isSameOrBefore(execDate, bucketUnit)) {
          return false;
        }

        if (isEqual(measure, SIM_ARRIVAL)) {
          const leadTime = params.data?.otdLt || 0;
          const ltBucketCount = RpUtil.getOtdLtByBucket(bucketUnit, leadTime) || 0;

          const freezeDate = execDate.clone().add(ltBucketCount, bucketUnit);

          // 실행일 ~ Freeze 기준일 사이의 입고 버켓은 수정 불가 (Freeze)
          if (bucket.isSameOrBefore(freezeDate, bucketUnit)) {
            return false;
          }
        }

      }
    }

    if (measure) {
      if (editTargetRef.current === SIM_RP_ORDER) {
        return isEqual(measure, SIM_RP_ORDER) || isEqual(measure, SIM_DEMAND_QTY);
      } else {
        return isEqual(measure, SIM_ARRIVAL) || isEqual(measure, SIM_DEMAND_QTY);
      }
    }

    return false;
  }

  const emptyValueFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value)) {
      return "";
    }

    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const valueFormatter = params => {
    return GridUtils.gridValueL10N(params.value);
  }

  const calendarValueFormatter = params => {
    const { data, column } = params;
    const value = data ? data[column.colId] : null;
    return value ? value : t("notConfigured");
  }

  const onCellClicked = params => {
    const { value } = params;
    if (value && !isEqual(value, t("notConfigured"))) {
      cycleCalendarService.get(value)
        .then(response => {
          setCycleCalendar(response.data);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          setCalendarModalOpen(true);
        });
    }
  }

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };


  const getAttributeColDefs = (isExcel = false) => {
    const itemColDefs = [];
    const attrDatas = dimensionAttrData?.attr;

    if (!attrDatas) {
      return itemColDefs;
    }

    for (let i = 0; i < attrDatas?.length; i ++) {
      const attrData = attrDatas[i];
      const attrId = `prod_${attrData}`;
      const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
      const attrNm = attrConfig?.item ? attrConfig.item[attrData] ?? attrData : attrData;
      const itemAttr = "itemAttr";
      const fieldNm = itemAttr + i;

      const itemAttrArry = isExcel ? [
        {
          headerName: attrNm,
          colId: attrId,
          field: fieldNm,
          cellClass: "stringType",
          width: 120,
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
        }] : [{
        headerName: attrNm,
        colId: attrId,
        showRowGroup: fieldNm,
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true,
        },
        width: 120,
        sortable: true,
        sort
      },
        {
          field: fieldNm,
          editable: false,
          pinned: "left", lockPinned: true,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      itemColDefs.push(...itemAttrArry);
    }
    return itemColDefs;
  }

  const getAttributesParam = () => {
    if (!dimensionAttrData?.attr || !Array.isArray(dimensionAttrData.attr)) {
      return {};
    }
    return dimensionAttrData.attr.reduce((acc, attrData) => {
      acc[attrData] = attrConfig?.item?.[attrData] ?? attrData;
      return acc;
    }, {});
  };

  const defaultColumnDefs = useMemo(() => {
    if (orderByItem === null) {
      return [];
    }

    const bucketPostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    const baseColumns = [
      {
        headerName: 'category', colId: `categoryName_${bucketPostFix}`, width: 140, showRowGroup: 'categoryNm',
        type: ["nonEditableColumn"], hide: colsHide, cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true }, headerClass: "header-color-01"
      },
      {
        headerName: 'orderMethod', colId: `rpPolicyCd_${bucketPostFix}`, width: 160, showRowGroup: 'policy',
        type: ["nonEditableColumn"], hide: colsHide, cellRenderer: 'agGroupCellRenderer', valueFormatter,
        cellRendererParams: { suppressCount: true }, headerClass: "header-color-01"
      },
      {
        headerName: 'targetStock', colId: `targetInvQty_${bucketPostFix}`, width: 110, showRowGroup: 'targetStock',
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], hide: colsHide,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, headerClass: "header-color-01"
      },
      {
        headerName: 'rop', colId: `reorderQty_${bucketPostFix}`, width: 110, showRowGroup: 'reorder',
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], hide: colsHide,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, headerClass: "header-color-01"
      },
      {
        headerName: 'lastMInvDays', colId: `invDays_${bucketPostFix}`, width: 110, showRowGroup: 'invDays',
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], hide: colsHide,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, headerClass: "header-color-01"
      },
      {
        headerName: 'orderCalendar', colId: `calendarGrp_${bucketPostFix}`, width: 150, showRowGroup: 'calendarNm', type: ["nonEditableColumn"],
        hide: colsHide, cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true, suppressDoubleClickExpand: true },
        onCellClicked, cellStyle: { cursor: 'pointer' }, headerClass: "header-color-01",
      },
      {
        headerName: 'moq', colId: `moqGrp_${bucketPostFix}`, width: 100, showRowGroup: 'moq',
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], hide: colsHide,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        headerClass: "header-color-01"
      },
      {
        headerName: 'ioq', colId: `ioqGrp_${bucketPostFix}`, width: 100, showRowGroup: 'ioq',
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], hide: colsHide,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        headerClass: "header-color-01"
      },
      {
        headerName: 'otdLtD', colId: `otdLtDays_${bucketPostFix}`, width: 110, showRowGroup: 'otdLt',
        type: ["numberColumn", "nonEditableColumn", "rightAligned"], hide: colsHide,
        cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        headerClass: "header-color-01"
      },
      { headerName: "category", field: "categoryNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "orderMethod", field: "policy", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueFormatter },
      { headerName: "targetStock", field: "targetStock", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueFormatter: emptyValueFormatter },
      { headerName: "rop", field: "reorder", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueFormatter: emptyValueFormatter },
      { headerName: "lastMInvDays", field: "invDays", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueFormatter: emptyValueFormatter },
      { headerName: "orderCalendar", field: "calendarNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueGetter: calendarValueFormatter },
      { headerName: "moq", field: "moq", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "ioq", field: "ioq", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "otdLtD", field: "otdLt", rowGroup: true, hide: true, suppressColumnsToolPanel: true, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
      {
        headerName: "measure", field: "measure", width: 170, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
        valueFormatter: params => t(params.value), cellStyle: cellStyleByMeasure, headerComponent: CustomHeaderMenuIcon,
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      }
    ]

    let defaultColoumns = [];

    const attributeColDefs = {
      // column header dimension modal
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          setDimenModalOpen(true);
        }
      },
    }

    const itemColDefs = getAttributeColDefs();

    if (orderByItem) {
      defaultColoumns = [
        {
          headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 140, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
          ,...attributeColDefs
        },
        {
          headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { suppressCount: true },
          cellStyle: locItemNoteCellStyle,
          headerComponent: CustomDetailHeader,
          headerComponentParams: {
            viewName, colsHide,
            detailColumns: [
              `categoryName_${bucketPostFix}`,
              `rpPolicyCd_${bucketPostFix}`,
              `targetInvQty_${bucketPostFix}`,
              `reorderQty_${bucketPostFix}`,
              `invDays_${bucketPostFix}`,
              `calendarGrp_${bucketPostFix}`,
              `moqGrp_${bucketPostFix}`,
              `ioqGrp_${bucketPostFix}`,
              `otdLtDays_${bucketPostFix}`]
          }
        },
        { headerName: "item", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: INV_CLASS_CD, field: INV_CLASS_CD, rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        {
          headerName: 'originLocation', colId: "originLocNmGrp", width: 140, showRowGroup: 'originLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
        },
        {
          headerName: 'destLocation', colId: "destLocNmGrp", width: 165, showRowGroup: 'destLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
        },
        { headerName: "originLocation", field: "originLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "destLocation", field: "destLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        {
          headerName: INV_CLASS_CD, colId: "invClassCdGrp", width: 120, showRowGroup: INV_CLASS_CD, pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { suppressCount: true, innerRenderer: AbcXyzChipInline }
        },
        ...baseColumns
      ]
    } else {
      defaultColoumns = [
        {
          headerName: 'originLocation', colId: "originLocNmGrp", width: 140, showRowGroup: 'originLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }
        },
        {
          headerName: 'destLocation', colId: "destLocNmGrp", width: 165, showRowGroup: 'destLocNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
        },
        { headerName: "originLocation", field: "originLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "destLocation", field: "destLocNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        {
          headerName: 'itemCd', colId: "itemCdGrp", cellClass: 'stringType', width: 140, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true }, cellStyle: locItemNoteCellStyle
          ,...attributeColDefs

        },
        {
          headerName: 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { suppressCount: true },
          cellStyle: locItemNoteCellStyle,
          headerComponent: CustomDetailHeader,
          headerComponentParams: {
            viewName, colsHide,
            detailColumns: [
              `categoryName_${bucketPostFix}`,
              `rpPolicyCd_${bucketPostFix}`,
              `targetInvQty_${bucketPostFix}`,
              `reorderQty_${bucketPostFix}`,
              `invDays_${bucketPostFix}`,
              `calendarGrp_${bucketPostFix}`,
              `moqGrp_${bucketPostFix}`,
              `ioqGrp_${bucketPostFix}`,
              `otdLtDays_${bucketPostFix}`]
          }
        },
        {
          headerName: INV_CLASS_CD, colId: "invClassCdGrp", width: 120, showRowGroup: INV_CLASS_CD, pinned: "left", lockPinned: true,
          type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: { suppressCount: true, innerRenderer: AbcXyzChipInline }
        },
        { headerName: "item", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        { headerName: INV_CLASS_CD, field: INV_CLASS_CD, rowGroup: true, hide: true, suppressColumnsToolPanel: true },
        ...baseColumns
      ]
    }

    const itemIdx = defaultColoumns.findIndex(col => isEqual(col.field, INV_CLASS_CD));
    defaultColoumns.splice(itemIdx + 1, 0, ...itemColDefs);

    return GridUtils.getColumnDefs(defaultColoumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderByItem, dimensionAttrData]);

  const defaultExportColumnDefs = useMemo(() => {
    const baseColumns = [
      { field: "categoryNm", colId: "categoryNmGrp", rowGroup: true, hide: true, },
      { field: "policy", colId: "policyGrp", rowGroup: true, hide: true, valueFormatter },
      { field: "targetStock", colId: "targetStockGrp", rowGroup: true, hide: true, valueFormatter: emptyValueFormatter },
      { field: "reorder", colId: "reorderGrp", rowGroup: true, hide: true, valueFormatter: emptyValueFormatter },
      { field: "invDays", colId: "invDaysGrp", rowGroup: true, hide: true, valueFormatter: emptyValueFormatter },
      { field: "calendarNm", colId: "calendarNmGrp", rowGroup: true, hide: true, valueGetter: calendarValueFormatter },
      { field: "moq", colId: "moqGrp", rowGroup: true, hide: true },
      { field: "ioq", colId: "ioqGrp", rowGroup: true, hide: true },
      { field: "otdLt", colId: "otdLtGrp", rowGroup: true, hide: true },
      { headerName: "category", field: "categoryNm", width: 140 },
      { headerName: "orderMethod", field: "policy", width: 110, valueFormatter },
      { headerName: "targetStock", field: "targetStock", width: 80, valueFormatter: emptyValueFormatter },
      { headerName: "rop", field: "reorder", width: 80, valueFormatter: emptyValueFormatter },
      { headerName: "lastMInvDays", field: "invDays", width: 80, valueFormatter: emptyValueFormatter },
      { headerName: "orderCalendar", field: "calendarNm", width: 140, valueGetter: calendarValueFormatter },
      { headerName: "moq", field: "moq", width: 80 },
      { headerName: "ioq", field: "ioq", width: 80 },
      { headerName: "otdLtD", field: "otdLt", width: 80 },
      { headerName: "measure", field: "measure", width: 160, valueFormatter: params => t(params.value)},
      { headerName: "isException", field: "isException", width: 100 }
    ];

    let defaultColoumns = [];
    const itemColDefs = getAttributeColDefs(true);

    if (orderByItem) {
      defaultColoumns = [
        { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true, },
        { field: "itemNm", colId: "itemNmGrp", rowGroup: true, hide: true, },
        { colId: "invClassCdGrp", rowGroup: true, hide: true },
        { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
        { headerName: "itemName", field: "itemNm", width: 140 },
        { headerName: INV_CLASS_CD, field: INV_CLASS_CD, width: 120, cellClass: 'stringType', cellRenderer: AbcXyzChip },
        { field: "originLocNm", colId: "originLocNmGrp",  rowGroup: true, hide: true, },
        { field: "destLocNm", colId: "destLocNmGrp", rowGroup: true, hide: true, },
        { headerName: "originLocation", field: "originLocNm", width: 140 },
        { headerName: "destLocation", field: "destLocNm", width: 140 },
        ...baseColumns
      ]
    } else {
      defaultColoumns = [
        { field: "originLocNm", colId: "originLocNmGrp",  rowGroup: true, hide: true, },
        { field: "destLocNm", colId: "destLocNmGrp", rowGroup: true, hide: true, },
        { headerName: "originLocation", field: "originLocNm", width: 140 },
        { headerName: "destLocation", field: "destLocNm", width: 140 },
        { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true, },
        { field: "itemNm", colId: "itemNmGrp", rowGroup: true, hide: true, },
        { colId: "invClassCdGrp", rowGroup: true, hide: true },
        { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
        { headerName: "itemName", field: "itemNm", width: 140 },
        { headerName: INV_CLASS_CD, field: INV_CLASS_CD, width: 120, cellClass: 'stringType', cellRenderer: AbcXyzChip },
        ...baseColumns
      ]
    }

    const itemIdx = defaultColoumns.findIndex(col => isEqual(col.field, INV_CLASS_CD));
    defaultColoumns.splice(itemIdx + 1, 0, ...itemColDefs);

    return GridUtils.getColumnDefs(defaultColoumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderByItem, dimensionAttrData]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ITEM_SEARCH_TO_AF = {
    itemCd: "itemCd",
    name: "itemName",
    itemHrchy: "category",
    invClassCd: "invClassCd",
    ...Object.fromEntries(Array.from({ length: 10 }, (_, idx) => {
      const attrCd = `attr${String(idx + 1).padStart(2, "0")}`;
      return [attrCd, attrCd];
    })),
  };

  const getUnsupportedItemKeys = () => Object.keys(itemFilterList ?? {})
    .filter(key => !isEmpty(itemFilterList[key]) && !ITEM_SEARCH_TO_AF[key]);

  const buildInitialFromCurrentFilters = () => {
    const nodes = [];
    const originLocs = filterValue?.originLocation?.map(l => l.name).filter(Boolean) ?? [];
    const destLocs = filterValue?.destLocation?.map(l => l.name).filter(Boolean) ?? [];
    const categories = Array.isArray(filterValue?.category) ? filterValue.category : (filterValue?.category ? [filterValue.category] : []);
    const exceptions = [...(appliedExceptions ?? [])];

    if (onlyExceptionApplied && !exceptions.includes("exceptionsOnly")) exceptions.push("exceptionsOnly");

    if (originLocs.length > 1) {
      nodes.push({ logicOperator: "OR", children: originLocs.map(name => ({ filterType: "string", headerName: "originLocation", operator: "equals", values: [name] })) });
    } else if (originLocs.length === 1) {
      nodes.push({ filterType: "string", headerName: "originLocation", operator: "equals", values: [originLocs[0]] });
    }

    if (destLocs.length > 1) {
      nodes.push({ logicOperator: "OR", children: destLocs.map(name => ({ filterType: "string", headerName: "destLocation", operator: "equals", values: [name] })) });
    } else if (destLocs.length === 1) {
      nodes.push({ filterType: "string", headerName: "destLocation", operator: "equals", values: [destLocs[0]] });
    }

    const [itemLogic, itemCompare] = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
    const itemOperator = itemCompare === filterType.notEqual.cd ? "notEqual" : "equals";
    const itemLogicOperator = itemLogic === filterType.and.cd ? "AND" : "OR";
    const itemNodes = [];

    for (const [key, values] of Object.entries(itemFilterList ?? {})) {
      const headerName = ITEM_SEARCH_TO_AF[key];
      const targets = (values ?? []).filter(Boolean);
      if (!headerName || targets.length === 0) {
        continue;
      }
      const leaf = value => ({ filterType: "string", headerName, operator: itemOperator, values: [value] });
      if (targets.length > 1) {
        itemNodes.push({ logicOperator: itemLogicOperator, children: targets.map(leaf) });
      } else {
        itemNodes.push(leaf(targets[0]));
      }
    }

    if (itemNodes.length > 1) {
      nodes.push({ logicOperator: itemLogicOperator, children: itemNodes });
    } else if (itemNodes.length === 1) {
      nodes.push(itemNodes[0]);
    }

    if (invClassCdList.length > 1) {
      nodes.push({ logicOperator: "OR", children: invClassCdList.map(cd => ({ filterType: "string", headerName: "invClassCd", operator: "equals", values: [cd] })) });
    } else if (invClassCdList.length === 1) {
      nodes.push({ filterType: "string", headerName: "invClassCd", operator: "equals", values: [invClassCdList[0]] });
    }

    for (const ex of exceptions) {
      nodes.push({ filterType: "select", headerName: "excludeCondition", filterOption: ["zeroReplenishment", "zeroPurchase", "zeroDemand", "zeroEoh", "exceptionsOnly"], operator: "equals", values: [ex] });
    }

    if (categories.length > 1) {
      nodes.push({ logicOperator: "OR", children: categories.map(cat => ({ filterType: "string", headerName: "category", operator: "equals", values: [cat.name] })) });
    } else if (categories.length === 1) {
      nodes.push({ filterType: "string", headerName: "category", operator: "equals", values: [categories[0].name] });
    }

    return nodes.length > 0 ? { logicOperator: "AND", children: [{ logicOperator: "AND", children: nodes }] } : { logicOperator: "AND", children: [] };
  };

  const handleFilterOpen = () => {
    if (!openFilter) {
      if (!isAFActive) {
        setPendingAdvancedFilters(buildInitialFromCurrentFilters());

        const unsupported = getUnsupportedItemKeys();
        if (unsupported.length > 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_UnsupportedItemFilterInAdvancedFilter", { fields: unsupported.map(key => t(key)).join(", ") }) });
        }
      } else {
        setPendingAdvancedFilters(null);
      }
      setNoteOpen(true);
    }
    setOpenFilter(prev => !prev);
  };

  const handleAdvancedFilterApply = (filters) => {
    const normalized = filters?.children?.length > 0 ? filters : null;
    setFilterValue({ originLocation: null, destLocation: null, category: null });
    setInvClassCdList([]);
    setAppliedExceptions([]);
    setOnlyExceptionApplied(false);
    setItemFilterList({});
    setItemCustomValue([]);
    setAdvancedFilters(normalized);
    setPendingAdvancedFilters(normalized);
    setIsAFActive(!!normalized);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createFilterViewState(viewName, null)));
  };

  const resetAdvancedFilter = () => {
    if (isAFActive) setIsAFActive(false);
    setPendingAdvancedFilters({ logicOperator: "AND", children: [] });
    setAdvancedFilters(null);
    setOpenFilter(false);
  };


  const handleFilterValueChange = (newValue) => {
    if (isAFActive && (newValue?.originLocation?.length > 0 || newValue?.destLocation?.length > 0 || newValue?.category?.length > 0)) {
      resetAdvancedFilter();
    }
    setFilterValue(newValue);
  };

  const handleInvClassCdListChange = (newList) => {
    resetAdvancedFilter();
    setInvClassCdList(newList);
  };

  const handleAppliedExceptionsChange = (newList) => {
    resetAdvancedFilter();
    setAppliedExceptions(newList);
  };

  const advancedFilterColumnDefs = [
    { headerName: "originLocation", filterType: "string" },
    { headerName: "destLocation", filterType: "string" },
    { headerName: "itemCd", filterType: "string" },
    { headerName: "itemName", filterType: "string" },
    { headerName: "invClassCd", filterType: "string", filterOption: dpABCConfig },
    { headerName: "categoryName", filterType: "string", filterName: "category" },
    { headerName: "orderMethod", filterType: "string", filterOption: RP_POLICY_LIST },
    { headerName: "targetStock", filterType: "number" },
    { headerName: "rop", filterType: "number" },
    { headerName: "lastMInvDays", filterType: "number" },
    { headerName: "orderCalendar", filterType: "string" },
    { headerName: "moq", filterType: "number" },
    { headerName: "ioq", filterType: "number" },
    { headerName: "otdLtD", filterType: "number" },
    ...Array.from({ length: 10 }, (_, idx) => {
      const order = idx + 1;
      const attrCd = `attr${String(order).padStart(2, "0")}`; // attr01 ~ attr10 (= RpColumn headerName)
      return {
        headerName: attrConfig?.item?.[attrCd] ?? `attr${order}`, // 설정된 라벨, 없으면 attr1~10
        filterType: "string",
        filterName: attrCd,
      };
    }),
    { headerName: "excludeCondition", filterType: "select", filterOption: ["zeroReplenishment", "zeroPurchase", "zeroDemand", "zeroEoh", "exceptionsOnly"] },
  ];

  const cleanupAdvancedFilters = (filter, columnDefs) => {
    const available = new Set(columnDefs.map(c => c.filterName || c.headerName));
    const clean = (node) => {
      if (node.children) {
        const newChildren = node.children.map(clean).filter(Boolean);
        return newChildren.length > 0 ? { ...node, children: newChildren } : null;
      }
      return available.has(node.headerName) ? node : null;
    };
    return clean(filter);
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (rpVersionFilter) {
      params.versionId = rpVersionFilter.id;
    }

    if (!isAFActive) {
      if (filterValue?.originLocation) {
        params.originLocCd = filterValue?.originLocation?.map(location => location?.locCd);
      }

      if (filterValue?.destLocation) {
        params.destLocCd = filterValue?.destLocation?.map(location => location?.locCd);
      }

      if (!isEmpty(filterValue?.category)) {
        params.categoryCds = filterValue?.category?.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
      } else {
        if (itemFilterList) {
          params.itemFilterList = itemFilterList;
          params.itemFilterType = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
        }
      }

      if (!isEmpty(invClassCdList)) {
        params.invClassCdList = invClassCdList;
      }

      if (!isEmpty(appliedExceptions)) {
        params.appliedExceptions = appliedExceptions;
      }
    } else {
      if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
        params.advancedFilters = advancedFilters;
      }
    }

    if (isPagination) {
      params.page = currentPage ? currentPage - 1 : 0;
      params.pageSize = pageSize ?? 10;
    }

    if (!isEmpty(measures)) {
      params.measures = measures;
    } else if (!isEmpty(p13Measures)) {
      params.measures = p13Measures;
    } else {
      params.measures = allMeasure;
    }

    params.pagination = isPagination;
    params.totalCount = resultTotalCount.current;
    params.bucketType = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
    params.orderByItem = orderByItem;
    //sort
    const sort = sortParams?.orderByColumn;
    const colIds = defaultColumnDefs?.map(colDef => colDef.colId);
    if (sort && !colIds.includes(sort)) {
      return { ...params, ...(isAFActive ? {} : { onlyExceptionApplied }) };
    }
    return { ...params, ...sortParams, ...(isAFActive ? {} : { onlyExceptionApplied }) };
  }

  const getIsException = (params, headers) => {
    const { column, data, value } = params;

    if (!isNil(data) && !isNil(value)) {
      const measure = data.originMeasure;
      if (!measure) {
        return false;
      }
      const locationItemNodes = params.node?.parent?.allLeafChildren || [];

      if (isEmpty(locationItemNodes)) {
        return false;
      }

      const numValue = Number(value);
      const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
      const replaceColId = column?.colId.replace(replacePostFix, "");
      const headerIndex = headers.indexOf(replaceColId);

      if (headerIndex !== -1 && value !== null) {
        const simDemandData = findMeasureData(locationItemNodes, SIM_DEMAND_QTY);
        const simDemandQty = simDemandData[headers[headerIndex]];
        return isEqual(measure, SIM_SHIPMENT_QTY) && simDemandQty > numValue;
      }
    }

    return false;
  }

  const setGridHeaders = (headers, headerMap) => {
    if (headers.length <= 0) {
      return;
    }

    bucketColumnDefs.current =  [];

    const bucketPostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;

    headers.forEach((header) => {
      bucketColumnDefs.current.push({
        headerName: headerMap[header], field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        type: ["nonEditableColumn", "rightAligned"],
        cellStyle: params => dataCellStyle(params, headers),
        editable: dynamicCellEditable,
        cellEditor: NumericEditor,
        valueFormatter: emptyValueFormatter,
        cellClassRules: {
          alertBackground: params => getIsException(params, headers)
        }
      });
    });
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...defaultColumnDefs, ...bucketColumnDefs.current]});
    columnApi?.applyColumnState({ state: p13nColumnState });
  }

  const checkRequireMeasures = (activeMeasures) => {
    for (let i = 0, arraySize = requiredMeasure.length; i < arraySize; i++) {
      const measure = requiredMeasure[i];
      if (activeMeasures.indexOf(measure) === -1) {
        activeMeasures.push(measure);
      }
    }
  }

  const setAttributeDatas = (resultsData) => {
    const itemAttr = "itemAttr";

    if (size(resultsData?.attributes) > 0) {
      for (let i = 0; i < resultsData.attributes.length; i++) {
        resultsData[itemAttr + i] = resultsData?.attributes?.[i];
      }
    }
  }

  const getIsExceptionData = (resultsData, headers) => {
    let isException = false;
    const demandQtyAdjPivotData = "demandQtyAdjPivotData";
    const salesQtyAdjPivotData = "salesQtyAdjPivotData";

    for (let idx = 0, len = headers.length; idx < len; idx++) {
      const simDemandVal = resultsData[demandQtyAdjPivotData] ? resultsData[demandQtyAdjPivotData][idx] : 0;
      const simShipmentVal = resultsData[salesQtyAdjPivotData] ? resultsData[salesQtyAdjPivotData][idx] : 0;
      if (simDemandVal > simShipmentVal) {
        isException = true;
      }
    }

    return isException;
  }

  const setGridDatas = (headers, results, measureCds = null) => {
    const datas = [];
    const rpOrderQtyPivotData = "rpOrderQtyPivotData";
    const arrivalQtyPivotData = "arrivalQtyPivotData";
    const arrivalQtyPlanPivotData = "arrivalQtyPlanPivotData";
    const arrivalQtyCfmPivotData = "arrivalQtyCfmPivotData";
    const demandQtyPivotData = "demandQtyPivotData";
    const salesQtyPivotData = "salesQtyPivotData";
    const salesQtyPlanPivotData = "salesQtyPlanPivotData";
    const salesQtyCfmPivotData = "salesQtyCfmPivotData";
    const salesQtyFcstPivotData = "salesQtyFcstPivotData";
    const salesQtyReplPivotData = "salesQtyReplPivotData";
    const bohQtyPivotData = "bohQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";
    const targetStockQtyPivotData = "targetStockQtyPivotData";
    const kitFcstDemandQtyPivotData = "kitFcstDemandQtyPivotData";
    const demandQtyAdjPivotData = "demandQtyAdjPivotData";
    const fcstDemandQtyPivotData = "fcstDemandQtyPivotData";
    const replDemandQtyPivotData = "replDemandQtyPivotData";
    const downstreamDemandQtySumPivotData = "downstreamDemandQtySumPivotData";
    const downstreamEohQtySumPivotData = "downstreamEohQtySumPivotData";
    const reorderQtyPivotData = "reorderQtyPivotData";
    const rpOrderQtyAdjPivotData = "rpOrderQtyAdjPivotData";
    const arrivalQtyAdjPivotData = "arrivalQtyAdjPivotData";
    const salesQtyAdjPivotData = "salesQtyAdjPivotData";
    const bohQtyAdjPivotData = "bohQtyAdjPivotData";
    const eohQtyAdjPivotData = "eohQtyAdjPivotData";
    const lastReleasedReplenishmentQtyPivotData = "lastReleasedReplenishmentQtyPivotData";
    const lastReleasedPurchaseQtyPivotData = "lastReleasedPurchaseQtyPivotData";
    const backorderQtyPivotData = "backorderQtyPivotData";
    const backorderQtyAdjPivotData = "backorderQtyAdjPivotData";

    const measure = isNull(measureCds) ? isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures
      : measureCds;

    const useShipmentTxn = rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN ?? false;

    checkRequireMeasures(measure);

    for (const resultsData of results) {
      const isExceptionData = getIsExceptionData(resultsData, headers);

      for (const measureName of measure) {
        const display = displayName?.[measureName];
        const data = { ...resultsData,
          measure: display != null && !isEmpty(display) ? display : measureName,
          originMeasure: measureName,
          isException: isExceptionData };
        setAttributeDatas(data);

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case SIM_RP_ORDER:
              data[header] = resultsData[rpOrderQtyAdjPivotData][idx];
              break;

            case RP_ORDER:
              data[header] = resultsData[rpOrderQtyPivotData][idx];
              break;

            case SIM_ARRIVAL:
              data[header] = resultsData[arrivalQtyAdjPivotData][idx];
              break;

            case ARRIVAL_QTY:
              data[header] = resultsData[arrivalQtyPivotData][idx];
              break;

            case ARRIVAL_QTY_PLAN:
              data[header] = resultsData[arrivalQtyPlanPivotData][idx];
              break;

            case ARRIVAL_QTY_CFM:
              data[header] = resultsData[arrivalQtyCfmPivotData][idx];
              break;

            case KIT_FCST_DEMAND_QTY:
              data[header] = resultsData[kitFcstDemandQtyPivotData][idx];
              break;

            case DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;

            case FCST_DEMAND_QTY:
              data[header] = resultsData[fcstDemandQtyPivotData][idx];
              break;

            case INTERNAL_DEMAND_QTY:
              data[header] = resultsData[replDemandQtyPivotData][idx];
              break;

            case DOWNSTREAM_DEMAND_QTY_SUM:
              data[header] = resultsData[downstreamDemandQtySumPivotData][idx];
              break;

            case DOWNSTREAM_EOH_QTY_SUM:
              data[header] = resultsData[downstreamEohQtySumPivotData][idx];
              break;

            case SIM_DEMAND_QTY:
              data[header] = resultsData[demandQtyAdjPivotData][idx];
              break;

            case SIM_SHIPMENT_QTY:
              data[header] = resultsData[salesQtyAdjPivotData][idx];
              break;

            case SHIPMENT_QTY:
              data[header] = resultsData[salesQtyPivotData][idx];
              break;

            case SHIPMENT_QTY_PLAN:
              data[header] = resultsData[salesQtyPlanPivotData][idx];
              break;

            case SHIPMENT_QTY_CFM:
              data[header] = resultsData[salesQtyCfmPivotData][idx];
              break;

            case SHIPMENT_QTY_FCST:
              data[header] = resultsData[salesQtyFcstPivotData][idx];
              break;

            case SHIPMENT_QTY_REPL:
              data[header] = resultsData[salesQtyReplPivotData][idx];
              break;

            case SIM_BOH:
              data[header] = resultsData[bohQtyAdjPivotData][idx];
              break;

            case BOH:
              data[header] = resultsData[bohQtyPivotData][idx];
              break;

            case SIM_EOH:
              data[header] = resultsData[eohQtyAdjPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            case TARGET_STOCK:
              data[header] = resultsData[targetStockQtyPivotData][idx];
              break;

            case ROP:
              data[header] = resultsData[reorderQtyPivotData][idx];
              break;

            case LAST_RELEASED_REPLENISHMENT_QTY:
              data[header] = resultsData[lastReleasedReplenishmentQtyPivotData][idx];
              break;

            case LAST_RELEASED_PURCHASE_QTY:
              data[header] = resultsData[lastReleasedPurchaseQtyPivotData][idx];
              break;

            case BACKORDER_QTY:
              data[header] = resultsData[backorderQtyPivotData][idx];
              break;

            case SIM_BACKORDER_QTY:
              data[header] = resultsData[backorderQtyAdjPivotData][idx];
              break;

            case INV_COV_DAYS:
              data[header] = getInvCovDays(idx, headers, resultsData, false);
              break;

            case SIM_INV_COV_DAYS:
              data[header] = getInvCovDays(idx, headers, resultsData, true);
              break;

            case SHIPMENT_SHORTAGE_QTY:
              data[header] = getShipmentShortageQty(idx, headers, resultsData);
              break;

            default:
              break;
          }
        }

        if (measureName === SHIPMENT_SHORTAGE_QTY && !useShipmentTxn) {
          continue;
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const removeNullVal = (params) => {
    for (const key of keys(params)) {
      if (isNull(params[key]) || isEqual(params[key], "")) delete params[key];
    }
    return params;
  }

  const getCustomConfig = () => {
    companyService.getCustomConfig()
      .then(response => {
        if (response.data) {
          setAttrConfig(response.data);
        } else {
          setAttrConfig({})
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveItemHrchies = () => {
    itemHrchyService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const { results } = response.data;
        if (results !== null) {
          setDefaultValue(results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrievePsiSimulation = (layoutParam = {}, measureCds = null, layoutData = layoutModalData) => {
    gridRef.current?.api?.setGridOption('loading', true);
    setSelectedRow([]);
    const params = getRequestParams();
    const finalParams = removeNullVal({ ...params, ...layoutParam });

    psiSimulationService.getAll(finalParams)
      .then(response => {
        const { pivotResult, totalPages, totalItems, noteResult } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;
        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          localDateHeaderMap.current = localDateHeader;
          setSimulationHeaders(localDateHeader);
          setGridHeaders(localDateHeader, headerMap);
          const datas = setGridDatas(localDateHeader, data, measureCds);
          setSimulationData(datas);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          setSimulationHeaders([]);
          setSimulationData([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
        noteKeySet.current = new Set(noteResult ?? []);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        let filterValues = {}
        if (layoutData?.reloadFalse && size(layoutData) < 2) {
          filterValues = filterValue;
        } else {
          filterValues = size(layoutData) > 2 ? {...filterValue, category: layoutData?.itemHrchy ?? null,
            originLocation: layoutData?.originLocation ? Array.isArray(layoutData?.originLocation) ? layoutData?.originLocation : [layoutData?.originLocation] : null,
            destLocation: layoutData?.destLocation ? Array.isArray(layoutData?.destLocation) ? layoutData?.destLocation : [layoutData?.destLocation] : null } : filterValue;
        }

        const reduxState = reduxUtil.createObjectState(viewName, { onlyExceptionApplied,
          layoutModalData: layoutData, currentPage, pageSize, initialFlg, orderByItem, invClassCdList: size(layoutData) > 2 ? layoutData?.invClassCdList : invClassCdList,
          appliedExceptions: size(layoutData) > 2 ? layoutData?.appliedExceptions : appliedExceptions, dimensionAttrData, advancedFilters: isAFActive ? advancedFilters : null,
          editTarget: editTargetRef.current || SIM_RP_ORDER, displayName});
        const filterState = reduxUtil.createFilterViewState(viewName, filterValues);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
      })
      .finally(() => {
        setSimulationUpdateDatas([]);
        gridRef.current?.api?.setGridOption('loading', false);
        layoutLoad.current = false;
        setTreeLoad(false);
      });
  }

  const isClearLayout = () => {
    let clearFlg = false;

    const { originLocation, destLocation, activeMeasures, itemHrchy, exceptionsOnly, invClassCdList, appliedExceptions } = layoutModalData;
    if (!isEqual(measures.sort(), keys(activeMeasures).sort())) {
      clearFlg = true;
    }

    const originLoc = !originLocation ? null : Array.isArray(originLocation) ? originLocation : [originLocation];
    const destLoc = !destLocation ? null : Array.isArray(destLocation) ? destLocation : [destLocation];

    if (!isEqual(originLoc?.map(val => val.locCd).sort(), filterValue?.originLocation?.map(val => val.locCd).sort())) {
      clearFlg = true;
    }
    if (!isEqual(destLoc?.map(val => val.locCd).sort(), filterValue?.destLocation?.map(val => val.locCd).sort())) {
      clearFlg = true;
    }

    const layoutHrchyCds = itemHrchy?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);
    const filterHrchyCds = filterValue?.category?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);

    if (!isEqual(layoutHrchyCds, filterHrchyCds)) {
      clearFlg = true;
    }
    if (!isNull(exceptionsOnly) && exceptionsOnly !== onlyExceptionApplied) {
      clearFlg = true;
    }
    if (!isEqual(layoutModalData?.itemFilterType, itemFilterType)) {
      clearFlg = true;
    }
    if (!isEqual(layoutModalData?.itemFilterList, itemFilterList)) {
      clearFlg = true;
    }
    if (!isEqual(layoutModalData?.invClassCdList, invClassCdList)) {
      clearFlg = true;
    }
    if (!isEqual(layoutModalData?.appliedExceptions, appliedExceptions)) {
      clearFlg = true;
    }

    if (clearFlg) {
      setLayoutModalData({});
      setCurrentPage(1);
      setMeasures(p13Measures);

      setTimeout(() => {
        retrievePsiSimulation({}, p13Measures, { reloadFalse: true });
      }, 100);
    }
  }

  const noteDefs = [
    { headerName: t("originLocation"), field: "originLocCd", sort: "asc", hide: false, cellClass: 'stringType' },
    { headerName: t("destLocation"), field: "destLocCd", sort: "asc", hide: false, cellClass: 'stringType' },
    { headerName: t("item"), field: "itemKey", sort: "asc", hide: false, cellClass: 'stringType' },
    { headerName: t("date"), field: "date", sort: "asc", hide: false },
    { headerName: t("measure"), field: "measure", sort: "asc", hide: false, valueFormatter },
    { headerName: t("notes"), field: "contentTxt", sort: "asc", hide: false },
  ];

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;

    if (size(layoutModalData) < 2) {
      if (currentPage > 1) {
        setCurrentPage(1);
      } else {
        retrievePsiSimulation();
      }
    } else {
      layoutLoad.current = true;
      isClearLayout();
    }
  }

  const retrieveP13n = () => {
    p13nService.getWithDisplayName(PSI_SIMULATION)
      .then(response => {
        const { data } = response;
        if (data && !isEmpty(data)) {
          const p13n = data[PSI_SIMULATION];
          if (p13n) {
            const p13nColumnState = p13n?.[gridId];
            setP13nColumnState(p13nColumnState);
          }
          const display = data["displayName"];
          if (display) {
            setDisplayName(display);
          }
          setOrderByItem(p13n?.orderByItem ?? false);

          if (p13n?.editTarget) {
            setEditTarget(p13n.editTarget);
            editTargetRef.current = p13n.editTarget;

            if (gridRef.current?.api) {
              gridRef.current.api.refreshCells({ force: true });
            }
          }
        } else {
          setOrderByItem(false);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const getDefaultCustomization = () => {
    const p13PageCd = `${viewName}_LAYOUT`;
    p13nService.getDefaultLayout(p13PageCd)
      .then(response => {
        const { data } = response;
        if (data?.layoutId) {
          setLayoutModalData(data);
        } else {
          retrievePsiSimulation({}, p13Measures, { reloadFalse: true });
        }
      })
      .catch(e => {
        initPageToRetrieve();
        debug(e);
      })
      .finally(() => {
        setInitialFlg(true);
        setInitLoad(true);
      })
  };

  const deleteVersion = () => {
    setBackDropOpen(true);
    runRpService.removeVersion(rpVersionFilter)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        setBackDropOpen(false);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        debug(e);
      });
  }

  const updateRpResultSummary = () => {
    planVersionService.updateSummary(rpVersionFilter.id)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_ConfirmSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
      })
      .then(() => {
        setBackDropOpen(false);
      });
  }

  const confirmVersion = (isSaved = false) => {
    if (!isSaved) {
      setBackDropOpen(true);
    }
    setIsRun(false);

    if (selectItemHrchy !== null && !isEmpty(selectItemHrchy)) {
      rpVersionFilter.targetItemHrchy = selectItemHrchy.map(item => item.hrchyCd);
      setDefaultValue(selectItemHrchy)
    }

    psiSimulationService.replanConfirmVersion(rpVersionFilter)
      .then(() => {
        updateRpResultSummary();
        retrievePsiSimulation();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
        setBackDropOpen(false);
      });
  }

  const updateSimulationData = (confirmationPending = false) => {
    setBackDropOpen(true);
    psiSimulationService.updateSimulationData(simulationUpdateDatas)
      .then(() => {
        if (confirmationPending) {
          confirmVersion(true);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          retrievePsiSimulation();
        }

        setSimulationUpdateDatas([]);
      })
      .catch(e => {
        debug(e);
        const detailErrorMsgInfo = e.response?.data?.message?.message ? { content: t(e.response?.data?.message?.message) } : {};
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed"), ...detailErrorMsgInfo });
      }).then(() => {
      if (!confirmationPending) {
        setBackDropOpen(false);
      }
    });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      if (isEmpty(simulationUpdateDatas)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      } else {
        updateSimulationData();
      }
    } else if (action === "DELETE") {
      deleteVersion();
    }
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    resetAdvancedFilter();
    setOnlyExceptionApplied(value);
  }

  const onGridReady = params => {
    setGridLoading(true);
    setColumnApi(params.columnApi)
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  }

  const setChart = (selectedRows) => {
    const selectedDatas = [];
    const dateFormat = [];

    chartSelectedRowsRef.current = selectedRows ?? [];

    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    for (const header of simulationHeaders) {
      dateFormat.push(gridHeaderMap.current[header]);
    }

    const measureList = isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures;
    const translationMeasure = displayNameMeasure(measureList)


    for (const row of selectedRows) {
      const chartDatas = {};
      let inputFlag = false;

      for (const header of simulationHeaders) {
        if (translationMeasure?.includes(row.measure)) {
          const headerName = gridHeaderMap.current[header];
          chartDatas[headerName] = row[header];
          chartDatas.measure = t(row.measure);

          inputFlag = true;
        }
      }

      if (inputFlag) {
        selectedDatas.push(chartDatas);
      }
    }
    setChartLabel(dateFormat);
    setChartData(selectedDatas);
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartSelectedRowsRef.current)) {
      return;
    }
    setChart(chartSelectedRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({nodes: selected, newValue: true, source: CHECKBOX_SELECTED});

    const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    const colId = e.column?.colId.replace(replacePostFix, "");

    if (!isEmpty(gridHeaderMap.current[colId])) {

      const matrix = [e.rowIndex, simulationHeaders.indexOf(colId)];
      const target = e.data;
      const cellDate = colId;
      target.baseDate = cellDate;
      target.versionDto = rpVersionFilter;
      const cellData = {
        cellDate,
        timeDimension: rpVersionFilter.planConfigObj.BUCKET_UNIT,
        versionDto: {...rpVersionFilter},
        measure: target.measure,
        originMeasure: target.originMeasure,
        itemCd: target.itemCd,
        itemNm: target.itemNm,
        originLocCd: target.originLocCd,
        originLocNm: target.originLocNm,
        destLocCd: target.destLocCd,
        destLocNm: target.destLocNm,
        baseDate: cellDate,
        cellMatrix: matrix,
        selectIL: item.cd,
        editable: dynamicCellEditable(e)
      };
      setNoteCellData(cellData);
    } else if (["itemCdGrp", "itemNmGrp", "originLocNmGrp", "destLocNmGrp"].includes(colId)) {
      const target = e.data;
      if (target) {
        setNoteCellData({
          timeDimension: rpVersionFilter.planConfigObj.BUCKET_UNIT,
          versionDto: {...rpVersionFilter},
          itemCd: target.itemCd,
          itemNm: target.itemNm,
          originLocCd: target.originLocCd,
          originLocNm: target.originLocNm,
          destLocCd: target.destLocCd,
          destLocNm: target.destLocNm,
          selectIL: item.cd
        });
      }
    }
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren;
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
      setSelectedRow(selectedRows);
    }
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0) {
      setChartData([]);
    } else {
      const firstNode = params.api.getDisplayedRowAtIndex(0);
      if (firstNode) {
        const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
        params.api.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
      }
    }
  }

  const applyMeasures = selectedMeasures => {
    setP13Measures(selectedMeasures)
    if (initialFlg) {
      setMeasures(selectedMeasures);
    }
  }

  /*
   * 표시명이 없으면(null/undefined/"") 측정치 코드 — setGridDatas 가 행의 measure 를 만드는 규칙과 같아야 한다.
   * RP Settings 를 한 번 저장한 테넌트는 표시명 설정이 {} 로 내려오는데, 예전 `!== null` 검사는 undefined 를
   * 통과시켜 차트 매칭 목록이 [undefined] 가 되고 차트가 비었다.
   */
  const getMeasureDisplayName = (measure) => {
    const display = displayName?.[measure];
    return display != null && !isEmpty(display) ? display : measure;
  }

  const displayNameMeasure = (measureList) => measureList.map(getMeasureDisplayName);

  const addUpdateDatas = datas => {
    const updateDatas = cloneDeep(simulationUpdateDatas);

    for (const data of datas) {
      const idx = findIndex(updateDatas, updateData => isEqual(updateData.id, data.id));
      if (idx > -1) {
        updateDatas[idx] = { ...updateDatas[idx], ...data };
      } else {
        updateDatas.push(data);
      }
    }

    setSimulationUpdateDatas(updateDatas);
  }

  const makeUpdateData = (headerIndex, idData, aggIdData, simDemandData, simRpOrderData, simArrivalData, simSalesData, simEohData, simBohData, verNumData, aggVerNumData, simBackorderData) => {
    const simulationHeader = simulationHeaders[headerIndex];

    const updateData = {};
    updateData.id = idData[headerIndex];
    updateData.aggId = aggIdData[headerIndex];
    updateData.demandQtyAdj =  simDemandData[simulationHeader];
    updateData.rpOrderQtyAdj = simRpOrderData[simulationHeader];
    updateData.arrivalQtyAdj = simArrivalData[simulationHeader];
    updateData.salesQtyAdj = simSalesData[simulationHeader];
    updateData.bohQtyAdj = simBohData[simulationHeader];
    updateData.eohQtyAdj = simEohData[simulationHeader];
    updateData.backorderQtyAdj = simBackorderData[simulationHeader];
    updateData.verNum = verNumData[headerIndex];
    updateData.aggVerNum = aggVerNumData[headerIndex];

    return updateData;
  }

  const onCellValueChanged = params => {
    const locationItemNodes = params.node.parent.allLeafChildren;
    const { column, data, value, node } = params;
    const { measure, otdLt, moq, ioq, targetStock } = data;

    const isShift = rpVersionFilter?.planConfigObj?.IMMEDIATE_SHIPMENT === "shipmentNext" ||
      rpVersionFilter?.planConfigObj?.IMMEDIATE_SHIPMENT === "notAllow";

    const isBackorder = rpVersionFilter?.planConfigObj?.BACKORDER === "allow";
    const useShipmentTxn = rpVersionFilter?.planConfigObj?.USE_SHIPMENT_TXN;

    let simulationStartIdx = 0;

    if (useShipmentTxn) {
      const planExecDate = rpVersionFilter?.planConfigObj?.PLAN_EXEC_DATE ?? moment().format('YYYY-MM-DD');
      const bucketUnit = rpVersionFilter?.planConfigObj?.BUCKET_UNIT || DAY;

      if (planExecDate) {
        const currentBucketIndex = simulationHeaders.findIndex(header => isDateInBucket(header, planExecDate, bucketUnit));
        simulationStartIdx = currentBucketIndex >= 0 ? currentBucketIndex + 1 : 0;
      }
    }

    if (measure && !isUndefined(otdLt)) {
      const numValue = Number(value);
      const replacePostFix = rpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + rpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
      const replaceColId = column.colId.replace(replacePostFix, "");
      const headerIndex = findIndex(simulationHeaders, header => isEqual(header, replaceColId));

      if (headerIndex > -1) {
        const simDemandData = findMeasureData(locationItemNodes, SIM_DEMAND_QTY);
        const simBackorderData = findMeasureData(locationItemNodes, SIM_BACKORDER_QTY);
        const simRpOrderData = findMeasureData(locationItemNodes, SIM_RP_ORDER);
        const simArrivalData = findMeasureData(locationItemNodes, SIM_ARRIVAL);
        const arrivalCfmData = findMeasureData(locationItemNodes, ARRIVAL_QTY_CFM);
        const simSalesData = findMeasureData(locationItemNodes, SIM_SHIPMENT_QTY);
        const salesCfmData = findMeasureData(locationItemNodes, SHIPMENT_QTY_CFM);
        const simBohData = findMeasureData(locationItemNodes, SIM_BOH);
        const simEohData = findMeasureData(locationItemNodes, SIM_EOH);
        const simInvCovDaysData = findMeasureData(locationItemNodes, SIM_INV_COV_DAYS);
        const targetStockData = findMeasureData(locationItemNodes, TARGET_STOCK);

        const idData = node.data.idPivotData;
        const aggIdData = node.data.aggIdPivotData;
        //const demandData = node.data.demandQtyPivotData;
        const verNumData = node.data.verNumPivotData;
        const aggVerNumData = node.data.aggVerNumPivotData;
        const expInvData = node.data.expInvQtyPivotData;

        const bucket = rpVersionFilter?.planConfigObj?.BUCKET_UNIT;
        const otdLtByBucket = RpUtil.getOtdLtByBucket(bucket, otdLt);
        const currentHeaderText = simulationHeaders[headerIndex];

        const bucketSize = simulationHeaders.length;

        // Sim. Arrival = 미착(확정) 입고 + 그 Bucket 에 도착하는 Sim. RP Order Qty
        // 이전 Arrival 에 델타(-기존 발주 +신규 발주)를 얹는 방식은
        // "Arrival 안에 OTD LT 만큼 앞선 Bucket 의 RP Order 가 들어있다" 는 불변식에 의존하는데,
        // 한쪽만 갱신하는 경로가 하나라도 있으면 음수 Arrival 로 번지고 스스로 복구되지 않기 때문에, 절대식으로 변경
        const setArrivalByRpOrder = (arrivalIdx, rpOrderQty) => {
          if (arrivalIdx < 0 || arrivalIdx >= bucketSize) {
            return;
          }

          const arrivalHeaderText = simulationHeaders[arrivalIdx];
          const arrivalCfmQty = arrivalCfmData?.[arrivalHeaderText] || 0;
          const arrivalQty = arrivalCfmQty + rpOrderQty;

          simArrivalData[arrivalHeaderText] = Math.max(arrivalQty, 0);
        }

        let demandQty = simDemandData[simulationHeaders[headerIndex]];
        let shipmentQtyCfm = 0;

        if (useShipmentTxn) {
          shipmentQtyCfm = salesCfmData[simulationHeaders[headerIndex]] || 0;
          demandQty = Math.max(demandQty - shipmentQtyCfm, 0);
        }

        const prevBackorderQty = headerIndex > 0 ? simBackorderData[simulationHeaders[headerIndex-1]] : 0;

        const datas = [];
        let recalculateIdx  = isShift ? headerIndex : headerIndex + 1;
        //let isInversion = false;

        let userOverrideIdx = -1;

        if (isEqual(measure, SIM_RP_ORDER)) {
          recalculateIdx = headerIndex + 1;

          const adjustedValue = RpUtil.getRpOrderQtyConsideredMoqAndIoq(numValue, moq, ioq);
          simRpOrderData[currentHeaderText] = adjustedValue;

          if (!isEqual(numValue, adjustedValue)) {
            setTimeout(() => { gridRef.current.api.flashCells({ rowNodes: [node], columns: [replaceColId], fadeDelay: 1000 }); }, 300);
          }

          // 수정한 Bucket 의 Sim. Arrival Qty, Sim. EOH 변경 후 다음 Bucket 의 Sim.BOH 변경
          // Sim. Arrival
          // 기존에 공급 실적에서 존재하는 입고량 + OTD Lt Days 에 맞게 조정된 Sim. RP Order Qty
          setArrivalByRpOrder(headerIndex + otdLtByBucket, adjustedValue);

          const simArrivalQty = simArrivalData[currentHeaderText];
          const simBohQty = simBohData[currentHeaderText];

          // Sim. Shipment Qty
          const usableArrivalQty = isShift ? 0 : simArrivalQty;

          const simSales = RpUtil.getShipmentQty(simBohQty, usableArrivalQty, demandQty + prevBackorderQty);
          simSalesData[currentHeaderText] = simSales + shipmentQtyCfm;

          // Sim. Backorder Qty
          simBackorderData[currentHeaderText] = isBackorder ? Math.max(prevBackorderQty + demandQty - simSales, 0) : 0;

          // Sim. EOH
          simEohData[currentHeaderText] = Number(simBohQty + simArrivalQty - simSales);

        } else if (isEqual(measure, SIM_DEMAND_QTY)) {
          const addedOtdLtDaysBucketIdx = headerIndex + otdLtByBucket;
          const targetStockQty = targetStockData[currentHeaderText];

          let planningIdx = headerIndex - otdLtByBucket;

          if (isShift) {
            planningIdx -= 1;
          }

          if (planningIdx >= simulationStartIdx) {
            if (otdLtByBucket !== 0) {
              // Sim. Shipment Qty
              const simBohQty = simBohData[currentHeaderText];
              const simSales = isShift ? RpUtil.getShipmentQty(simBohQty, 0, demandQty + prevBackorderQty) : demandQty + simBackorderData[currentHeaderText];
              simSalesData[currentHeaderText] = simSales + shipmentQtyCfm; // 1. 출하 변경

              // isInversion = true;
              recalculateIdx = planningIdx;
            } else { // otd Lt 0 Bucket Case
              const planningHeaderText = simulationHeaders[planningIdx];
              const simBohQty = isShift ? 0 : simBohData[currentHeaderText]; // Shift 인 경우, Sim. BOH 는 이전 Bucket Arrival 이므로 고려 x
              const prevBackorderQty = headerIndex > 0 ? simBackorderData[simulationHeaders[planningIdx]] : 0;

              const forecastDemandQty = 0;
              const expectedShipmentQty = demandQty + prevBackorderQty; // otd lt 0 이니 shipment 맞춤

              let openRpOrder = RpUtil.getOpenRpOrder(simRpOrderData, simulationHeaders, arrivalCfmData, headerIndex, otdLtByBucket);

              const currentEohQty = simBohQty;
              const netRqdQty = RpUtil.getNetRequiredQtyByOrderMethod(data, currentEohQty, targetStockQty, forecastDemandQty, expectedShipmentQty, openRpOrder, moq);
              const recalculatedRpOrderQty = RpUtil.getRpOrderQtyConsideredMoqAndIoq(netRqdQty, moq, ioq);

              simRpOrderData[planningHeaderText] = recalculatedRpOrderQty; // 1. 수요에 맞춘 RP ORDER 먼저 변경

              setArrivalByRpOrder(planningIdx + otdLtByBucket, recalculatedRpOrderQty); // 2. Arrival 변경

              const simArrivalQty = simArrivalData[planningHeaderText];

              const simSales = RpUtil.getShipmentQty(simBohQty, simArrivalQty, demandQty);
              simSalesData[currentHeaderText] = simSales + shipmentQtyCfm; // 3. 출하 변경

              // Sim. EOH
              simEohData[currentHeaderText] = Number(simBohQty + simArrivalQty - simSales);  // 4. EOH 변경

              if (isShift) {
                const replenishbucketBohQty  = simBohData[planningHeaderText];
                const arrivalBucketEohQty = replenishbucketBohQty + simArrivalQty - simSalesData[planningHeaderText];

                simEohData[planningHeaderText] = arrivalBucketEohQty; // Shift 인 경우, Sim. EOH 는 BOH - Sales
                simBohData[currentHeaderText] = arrivalBucketEohQty;
              }

              simBackorderData[currentHeaderText] = isBackorder ? Math.max(demandQty - simSales, 0) : 0; // 5. Backorder 변경
            }
          } else {
            if (headerIndex === 0) {
              const simArrivalQty = simArrivalData[currentHeaderText];
              const simBohQty = simBohData[currentHeaderText];

              const usableArrivalQty = isShift ? 0 : simArrivalQty;

              // Sim. Shipment Qty
              const simSales = RpUtil.getShipmentQty(simBohQty, usableArrivalQty, demandQty + prevBackorderQty);
              simSalesData[currentHeaderText] = simSales + shipmentQtyCfm; // 1. 출하 변경

              // Sim. Backorder Qty
              const simBackorderQty = isBackorder ? Math.max(demandQty - simSales, 0) : 0;
              simBackorderData[currentHeaderText] = simBackorderQty;

              // Sim. EOH
              simEohData[currentHeaderText] = Number(simBohQty + simArrivalQty - simSales);  // 2. EOH 변경
              // Sim. RP Order Qty
              const rpOrderQty = simRpOrderData[currentHeaderText];

              const arrivalQty = otdLtByBucket === 0 ? simArrivalData[currentHeaderText] - rpOrderQty : simArrivalData[currentHeaderText];
              const forecastDemandQty = 0;

              const lastSalesData = {...simDemandData};

              if (useShipmentTxn) {
                for (let i = 0; i <= otdLtByBucket + (isShift ? 1 : 0); i++) {
                  const currentShipmentQtyCfm = salesCfmData[simulationHeaders[i]] || 0;
                  lastSalesData[simulationHeaders[i]] = Math.max(simDemandData[simulationHeaders[i]] - currentShipmentQtyCfm, 0);
                }
              }

              if (!isShift) {
                let expectedShipmentQty = RpUtil.getShipmentQtyUpToOtdLtDays(lastSalesData, simulationHeaders, headerIndex, addedOtdLtDaysBucketIdx, isShift);
                expectedShipmentQty += simBackorderQty;

                const openRpOrder = RpUtil.getOpenRpOrder(simRpOrderData, simulationHeaders, arrivalCfmData, headerIndex, otdLtByBucket);
                const currentEohQty = simBohQty + arrivalQty - simSales;

                const netRqdQty = RpUtil.getNetRequiredQtyByOrderMethod(data, currentEohQty, targetStockQty, forecastDemandQty, expectedShipmentQty, openRpOrder, moq);
                const recalculatedRpOrderQty = RpUtil.getRpOrderQtyConsideredMoqAndIoq(netRqdQty, moq, ioq);

                simRpOrderData[currentHeaderText] = recalculatedRpOrderQty; // 3. RP ORDER 변경

                // Sim. Arrival
                // 기존에 공급 실적에서 존재하는 입고량 + OTD Lt Days 에 맞게 조정된 Sim. RP Order Qty
                setArrivalByRpOrder(addedOtdLtDaysBucketIdx, recalculatedRpOrderQty);
              }
            } else {
              recalculateIdx = simulationStartIdx;
              //isInversion = true;
            }
          }
        } else if (isEqual(measure, SIM_ARRIVAL)) {
          const arrivalCfmQty = arrivalCfmData?.[currentHeaderText] || 0;
          const validNumValue = Math.max(numValue, arrivalCfmQty);

          // 1. 발주 시점 역산 (Arrival을 수정할 때는 isShift 여부와 관계없이)
          const planningIdx = headerIndex - otdLtByBucket;

          // 재계산 시작점은 역산 위치부터
          recalculateIdx = Math.max(planningIdx, simulationStartIdx);

          // 사용자가 직접 수정한 Arrival 수량 유지위해 저장
          userOverrideIdx = planningIdx;

          if (planningIdx >= simulationStartIdx) {
            const planningHeaderText = simulationHeaders[planningIdx];

            // 입력한 입고량에서 미착(확정) 입고분을 제외한 나머지가 그 시점에 필요한 발주량
            const targetRpOrderQty = Math.max(validNumValue - arrivalCfmQty, 0);
            const adjustedRpOrderQty = RpUtil.getRpOrderQtyConsideredMoqAndIoq(targetRpOrderQty, moq, ioq);

            // 과거 발주량 보정 및 현재 Arrival 확정 (MOQ/IOQ 보정분까지 반영)
            simRpOrderData[planningHeaderText] = adjustedRpOrderQty;
            setArrivalByRpOrder(headerIndex, adjustedRpOrderQty);
          } else {
            // 발주 시점이 고정(실적/Freeze) 구간이라 RP Order 를 보정할 수 없으므로 입력값만 반영
            simArrivalData[currentHeaderText] = validNumValue;
          }

          // 2. 현재 버킷 지표 계산 (Shift 영향 반영)
          const simArrivalQty = simArrivalData[currentHeaderText];
          const simBohQty = simBohData[currentHeaderText];

          // Shift가 true이면 현재 들어온 입고량 당일 출하(simSales)에 사용 불가
          const usableArrivalQty = isShift ? 0 : simArrivalQty;

          // 출하량 계산 (BOH + 가용입고량 범위 내에서만 출하)
          const simSales = RpUtil.getShipmentQty(simBohQty, usableArrivalQty, demandQty + prevBackorderQty);
          simSalesData[currentHeaderText] = simSales + shipmentQtyCfm;
          simBackorderData[currentHeaderText] = isBackorder ? Math.max(prevBackorderQty + demandQty - simSales, 0) : 0;

          // EOH 계산: 출하되지 못한 Arrival은 EOH에 그대로 남아 다음 버킷 BOH로 넘어감
          simEohData[currentHeaderText] = Number(simBohQty + simArrivalQty - simSales);
        }

        // const updateData = makeUpdateData(headerIndex, idData, aggIdData, simDemandData, simRpOrderData, simArrivalData, simSalesData, simEohData, simBohData, verNumData, simBackorderData);
        // updateData.destLocCd = data.destLocCd;
        // updateData.itemCd = data.itemCd;

        // datas.push(updateData);

        const simulationDemandData = [];

        for (let idx = 0; idx < bucketSize; idx++) {
          simulationDemandData.push(simDemandData[simulationHeaders[idx]]);
        }

        // 수정 된 이후 Bucket 에 변경된 Sim. BOH, Sim. ArrivalQty 로 Sim. RP Order, Sim. EOH 재계산
        for (let idx = recalculateIdx; idx < bucketSize; idx++) {
          if (idx > simulationStartIdx) {
            const prevEoh = simEohData[simulationHeaders[idx - 1]];
            if (expInvData[idx - 1] > 0) {
              simBohData[simulationHeaders[idx]] = RpUtil.getBohWithExpInvQtyRemoved(simSalesData, expInvData, simulationHeaders, prevEoh, idx - 1);
            } else {
              simBohData[simulationHeaders[idx]] = prevEoh;
            }
          }

          const isUserOverride = (idx === userOverrideIdx);
          const addedOtdLtDaysBucketIdx = idx + otdLtByBucket;
          // OTD LT(D) 만큼 Shipment 재설정
          RpUtil.resetShipmentQtyUpToOtdLtDays(simBohData, simArrivalData, simulationDemandData, simSalesData, simBackorderData, simulationHeaders, idx, addedOtdLtDaysBucketIdx, isShift, isBackorder);

          const headerText = simulationHeaders[idx];
          const rpOrderQty = simRpOrderData[headerText];
          const bohQty = simBohData[headerText];
          const currentArrivalQty = simArrivalData[headerText];
          let currentDemandQty = simDemandData[headerText];
          const prevBackorderQty = idx > 0 ? simBackorderData[simulationHeaders[idx-1]] : 0;
          let shipmentQtyCfm = 0;
          if (useShipmentTxn) {
            shipmentQtyCfm = salesCfmData[headerText] || 0;
            currentDemandQty = Math.max(currentDemandQty - shipmentQtyCfm, 0);
          }
          const usableArrivalQty = isShift ? 0 : currentArrivalQty;

          const targetStockQty = targetStockData[simulationHeaders[addedOtdLtDaysBucketIdx]] ?? targetStock ?? 0;

          // Sim. Shipment Qty
          const salesQty = (otdLtByBucket === 0 && !isShift && !isUserOverride) ? prevBackorderQty + currentDemandQty : RpUtil.getShipmentQty(bohQty, usableArrivalQty, currentDemandQty + prevBackorderQty);
          simSalesData[headerText] = salesQty + shipmentQtyCfm;

          const shipmentQty = salesQty;

          // 같은 Bucket 에 입고가 들어오는 경우 공급 실적에서 존재하는 입고량만 가져오기 위해 기존 Sim. RP Order 제함
          const arrivalQty = otdLtByBucket === 0  ? simArrivalData[headerText] - rpOrderQty : simArrivalData[headerText];

          const lastSalesData = {...simDemandData};

          if (!isBackorder) {
            for (let i = idx; i < idx + otdLtByBucket + (isShift ? 1 : 0); i++) {
              lastSalesData[simulationHeaders[i]] = simSalesData[simulationHeaders[i]];
            }
          }

          if (useShipmentTxn) {
            for (let i = idx; i <= idx + otdLtByBucket + (isShift ? 1 : 0); i++) {
              const currentShipmentQtyCfm = salesCfmData[simulationHeaders[i]] || 0;
              lastSalesData[simulationHeaders[i]] = Math.max(simDemandData[simulationHeaders[i]] - currentShipmentQtyCfm, 0);
            }
          }

          // const forecastDemandQty = RpUtil.getForecastDemandQty(demandData, addedOtdLtDaysBucketIdx, otdLtByBucket);
          const forecastDemandQty = 0;
          let expectedShipmentQty = RpUtil.getShipmentQtyUpToOtdLtDays(lastSalesData, simulationHeaders, idx, addedOtdLtDaysBucketIdx, isShift);

          // Sim. Backorder
          const simBackorderQty = isBackorder ? Math.max(currentDemandQty + prevBackorderQty - salesQty, 0) : 0;

          //if (isInversion) {
          expectedShipmentQty += simBackorderQty;
          // }

          if (!isUserOverride) {
            const openRpOrder = RpUtil.getOpenRpOrder(simRpOrderData, simulationHeaders, arrivalCfmData, idx, otdLtByBucket, isShift);

            const currentEohQty = bohQty + arrivalQty - shipmentQty;
            const netRqdQty = RpUtil.getNetRequiredQtyByOrderMethod(data, currentEohQty, targetStockQty, forecastDemandQty, expectedShipmentQty, openRpOrder, moq);
            const recalculatedRpOrderQty = RpUtil.getRpOrderQtyConsideredMoqAndIoq(netRqdQty, moq, ioq);

            simRpOrderData[headerText] = recalculatedRpOrderQty;

            // Sim. Arrival
            // 기존에 공급 실적에서 존재하는 입고량 + OTD Lt Days 에 맞게 조정된 Sim. RP Order Qty
            setArrivalByRpOrder(addedOtdLtDaysBucketIdx, recalculatedRpOrderQty);
          }

          const lastArrivalQty = otdLtByBucket === 0 ? simArrivalData[simulationHeaders[addedOtdLtDaysBucketIdx]] : currentArrivalQty;

          simBackorderData[headerText] = simBackorderQty;

          // Sim. EOH
          simEohData[headerText] = Number(bohQty + lastArrivalQty - salesQty);

          simInvCovDaysData[headerText] = getSimInvCovDays(idx, simulationHeaders, simEohData, simDemandData);

          // const recalculatedData = makeUpdateData(idx, idData, aggIdData, simDemandData, simRpOrderData, simArrivalData, simSalesData, simEohData, simBohData, verNumData, simBackorderData);
          // recalculatedData.destLocCd = data.destLocCd;
          // recalculatedData.itemCd = data.itemCd;

          // datas.push(recalculatedData);
        }

        // result agg 업데이트를 위해, 업데이트 범위 전체 bucket 으로 변경
        for (let idx = 0; idx < bucketSize; idx++) {
          const adjustUpdateDatas = makeUpdateData(idx, idData, aggIdData, simDemandData, simRpOrderData, simArrivalData, simSalesData, simEohData, simBohData, verNumData, aggVerNumData, simBackorderData);
          adjustUpdateDatas.originLocCd = data.originLocCd;
          adjustUpdateDatas.destLocCd = data.destLocCd;
          adjustUpdateDatas.itemCd = data.itemCd;

          datas.push(adjustUpdateDatas);
        }

        addUpdateDatas(datas);

        gridRef.current.api.redrawRows({ rowNodes: locationItemNodes });
        node.setSelected(false);
        node.setSelected(true);
      }
    }
  }

  const retrieveAttributeConfig = () => {
    p13nService.get(`${viewName}_ETC`)
      .then(response => {
        if (response.data) {
          const attrConfig = response?.data?.ATTRIBUTE;
          setDimensionAttrData(attrConfig ? response.data.ATTRIBUTE : {});
        }
      })
      .catch(e => {
        debug(e);
        setDimensionAttrData({});
      });
  };

  /*
   * 세 패널이 같은 줄에 나란히 서므로 서로를 닫을 이유가 없다.
   *   예전에는 이것만 그리드 위에 떠 있어서 겹치지 않게 노트·고급 필터를 닫았다.
   */
  const handleLayoutOpen = () => {
    setLayoutOpen(!layoutOpen);
  };

  const handleReset = () => {
    layoutLoad.current = true;
    resetAdvancedFilter();
    setFilterValue(null);
    setItemCustomValue([]);
    setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    setItemFilterList({});
    setInvClassCdList([]);
    setAppliedExceptions([]);
    setTreeLoad(true);
    setMeasures(p13Measures);
    setLayoutModalData({});
    setOnlyExceptionApplied(false);
    resultTotalCount.current = 0;

    const clearParams = {
      item: null,
      categoryCds: null,
      destLocCd: null,
      originLocCd: null,
      onlyExceptionApplied: false,
      invClassCdList: [],
      itemFilterList: {},
      appliedExceptions: [],
      advancedFilters: null
    };

    const filter = null;
    const filterState = reduxUtil.createFilterViewState(viewName, filter);
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...filterState, advancedFilters: null }));

    setTimeout(() => {
      retrievePsiSimulation(clearParams, p13Measures, {});
    }, 100);
  }

  const getSortMeasures = (measureMap) => {
    if (!isEmpty(measureMap)) {
      const meaVals = values(measureMap);
      const sortVals = sortBy(meaVals, "seqNum");
      return sortVals.map(mea => mea.measureCd);
    }
    return [];
  }

  useEffect(() => {
    let filter = filterValue;
    if (filterData.filterList) {
      resetAdvancedFilter();
      setItemFilterList(filterData.filterList ?? {});
      setItemCustomValue([]);
      if (isEmpty(filterValue?.category)) {
        //reload
        setFilterValue({ ...filterValue, category: []});
        filter = { ...filterValue, category: []};
      } else {
        setTreeLoad(true);
      }
      setItemFilterType(filterData.filterType)
    }

    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, filter, itemFilterType: filterData?.filterType, itemFilterList: filterData?.filterList}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (itemFilterList && Object.keys(itemFilterList)?.length > 0) {
      setItemFilterColor(muicolor.INFO);
    } else {
      setItemFilterColor(muicolor.DEFAULT);
    }
    if (reduxViewState) {
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, itemFilterType, itemFilterList }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilterList]);

  useEffect(() => {
    if (layoutModalData?.clearLayout) {
      handleReset();
    }
    if (layoutLoad.current) {
      return;
    }
    if (size(layoutModalData) > 2 && !layoutModalData?.reloadFalse) {
      layoutLoad.current = true;
      setCurrentPage(1);

      //기존 layout과의 호환때문에 기존 item, originLocation, destLocation 남겨둠
      const { originLocation, destLocation, activeMeasures, itemHrchy, itemHrchyObj, item, exceptionsOnly, invClassCdList, appliedExceptions } = layoutModalData;

      const originLoc = !originLocation ? [] : Array.isArray(originLocation) ? originLocation : [originLocation];
      const destLoc = !destLocation ? [] : Array.isArray(destLocation) ? destLocation : [destLocation];

      let filter = cloneDeep(filterValue);
      filter = {...filterValue, originLocation: originLoc, destLocation: destLoc }

      const categoryies = itemHrchyObj ?? itemHrchy ?? null;

      const layoutHrchyCds = categoryies?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);
      const filterHrchyCds = filterValue?.category?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);

      if (!isEqual(filterHrchyCds, layoutHrchyCds)) {
        filter = {...filter, category: categoryies};
        setItemFilterList({});
        setItemCustomValue(categoryies ?? []);
      }
      setFilterValue({ ...filter });

      if (!isEmpty(item)) {
        layoutModalData.itemFilterList = {itemCd: [item]}
        layoutModalData.itemFilterType = [filterType.or.cd, filterType.equal.cd];
      }
      setItemFilterType(layoutModalData?.itemFilterType)
      setItemFilterList(layoutModalData?.itemFilterList)
      setOnlyExceptionApplied(exceptionsOnly);
      setInvClassCdList(invClassCdList ?? []);
      setAppliedExceptions(appliedExceptions ?? []);

      const measureCds = getSortMeasures(activeMeasures);
      checkRequireMeasures(measureCds);
      setMeasures(measureCds);

      const layoutData = { ...layoutModalData, reloadFalse: true };
      setLayoutModalData(layoutData);

      const layoutParams = {
        itemFilterType: layoutModalData.itemFilterType ?? [],
        itemFilterList: layoutModalData.itemFilterList ?? {},
        categoryCds: categoryies?.map((category) => category?.hrchyCd) ?? null,
        destLocCd: destLoc ? destLoc.map(val=>val.locCd) : [],
        originLocCd: originLoc ? originLoc.map(val=>val.locCd) : [],
        onlyExceptionApplied: exceptionsOnly ?? false,
        totalCount: null,
        invClassCdList: invClassCdList ?? [],
        appliedExceptions: appliedExceptions ?? [],
        page: 0,
        advancedFilters: null,
        itemLevelCd: null,
      }

      const reduxState = reduxUtil.createObjectState(viewName, { onlyExceptionApplied,
        layoutModalData: layoutData, measures, currentPage, pageSize, initialFlg, orderByItem, invClassCdList: invClassCdList ?? [], appliedExceptions: appliedExceptions ?? [], editTarget: editTargetRef.current || SIM_RP_ORDER });
      const filterState = reduxUtil.createFilterViewState(viewName, filter);
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));

      // After the conditions are set
      setTimeout(() => {
        retrievePsiSimulation(layoutParams, measureCds);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutModalData]);

  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && (!isEmpty(measures) || !isEmpty(p13Measures)) && !layoutLoad.current && initLoad && dimensionAttrData !== null && attrConfig !== null) {
      retrievePsiSimulation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, currentPage, pageSize, sortParams, dimensionAttrData]);

  useEffect(() => {
    if (!isEmpty(localDateHeaderMap.current) && !isEmpty(gridHeaderMap.current)) {
      setGridHeaders(localDateHeaderMap?.current, gridHeaderMap?.current);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invCovDaysConfig, simulationData]);

  useEffect(() => {
    if (gridLoading && !isEmpty(rpVersionFilter) && !layoutLoad.current && !isEmpty(p13Measures) && orderByItem !== null) {
      if (!isEmpty(filterValue?.category)) {
        setItemFilterList({});
        setItemFilterType([filterType.or.cd, filterType.equal.cd]);
      }
      if (!initLoad && !initialFlg) {
        getDefaultCustomization();
        setInitLoad(true);
      } else {
        initPageToRetrieve();
        setInitLoad(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpVersionFilter?.id, filterValue, measures, onlyExceptionApplied, p13Measures, orderByItem, invClassCdList, appliedExceptions, advancedFilters, isAFActive]);


  useEffect(() => {
    if (!advancedFilters?.children?.length) return;
    const cleaned = cleanupAdvancedFilters(advancedFilters, advancedFilterColumnDefs);
    setAdvancedFilters(cleaned?.children?.length ? cleaned : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData, attrConfig]);

  useEffect(() => {
    retrieveP13n();
    retrieveItemHrchies();
    getCustomConfig();
    retrieveAttributeConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isRun) {
      if (isEmpty(simulationUpdateDatas)) {
        confirmVersion();
      } else {
        updateSimulationData(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRun])

  const saveOrderByItem = (orderByItemParam) => {
    const saveData = { pageCd: PSI_SIMULATION, configObj: { orderByItem: orderByItemParam } };

    p13nService.upsert(saveData)
      .then(() => {
        debug(t("MSG_SaveSuccess"));
      })
      .catch(e => {
        debug(e);
      });
  }

  useEffect(() => {
    if (size(updateNoteData) > 0) {
      const newUpdateKey = CodeUtil.getNoteKey(viewName, updateNoteData);
      if (updateNoteData.deleteFlg) {
        noteKeySet.current.delete(newUpdateKey);
      } else {
        noteKeySet.current.add(newUpdateKey);
      }
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...defaultColumnDefs, ...bucketColumnDefs.current], initState: true });
      setUpdateNoteData({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoteData]);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationData, viewName]);

  const handleNoteModal = () => {
    setNoteOpen(!noteOpen);
    if (noteOpen) {
      if (openFilter) {
        setOpenFilter(false);
      }
    }
    if (!noteOpen && noteUpdateFlg) {
      setNoteUpdateFlg(false);
    }
  };

  const changeSortDimen = () => {
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [], initState: true });
    setOrderByItem(!orderByItem);
    saveOrderByItem(!orderByItem);
  }

  const isNoRowSelected  = isEmpty(selectedRow) || isNull(selectedRow);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processDataFromClipboard = (params) => {
    const data = params.data;
    const cleaned = data.filter(row =>row && row.some(cell => cell !== null && cell.toString().trim() !== ''));

    const isSingleCell = cleaned.length === 1 && cleaned[0].length === 1;

    if (isSingleCell || cleaned.length === 0) {
      const cellValue = data[0][0];

      if (cellValue === null || cellValue.toString().trim() === '') {
        return [[0]];
      }

      return cleaned;
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_MultiCellPasteNotAllowed") });
      return null;
    }
  }

  const getSortState = (params) => {
    const columnState = params.api.getColumnState();
    let sortState = sortParams ?? {};
    const sortColumn = find(columnState, column => isEqual(column.sort, "asc") || isEqual(column.sort, "desc"));
    if (isEqual(params.source, "uiColumnSorted")) {
      let sortColNm = sortColumn?.colId;
      if (includes(sortColumn?.colId, "item")) {
        sortColNm = sortColumn.colId.replace("item", "prod_");
      }
      sortState = { orderByColumn: sortColNm, sortType: sortColumn?.sort };
    }
    setSortParams(sortState);
  }

  const settingSortParams = (params) => {
    if (!dimenModalOpen) {
      getSortState(params);
    }
  }

  const saveEditTarget = (targetParam) => {
    const saveData = { pageCd: PSI_SIMULATION, configObj: { editTarget: targetParam } };
    p13nService.upsert(saveData)
      .then(() => {
        debug(t("MSG_SaveSuccess"));
      })
      .catch(e => {
        debug(e);
      });
  }

  const handleToggleEditTarget = useCallback(() => {
    const nextEditTarget = editTarget === SIM_RP_ORDER ? SIM_ARRIVAL : SIM_RP_ORDER;
    setEditTarget(nextEditTarget);
    editTargetRef.current = nextEditTarget;
    saveEditTarget(nextEditTarget);

    const gridApi = gridRef.current?.api;
    if (gridApi) {
      gridApi.stopEditing(false);
      gridApi.refreshCells({ force: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTarget]);

  return (
    <>
      <FilterContainer>
        <Box>
          <Tooltip title={t("dimension")}>
            <IconButton
              size="small"
              onClick={changeSortDimen}
            >
              <Transform />
            </IconButton>
          </Tooltip>
        </Box>
        <RpVersionFilter viewName={viewName} rpVersionValue={rpVersionFilter} onChangeFilter={setRpVersionFilter} statusFilters={[PLANNED, SIMULATION]} />
        <LocationTreeFilter viewName={viewName} title={"originLocation"} filterValue={filterValue === null || isEmpty(filterValue?.originLocation) ? null : filterValue?.originLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <LocationTreeFilter viewName={viewName} title={"destLocation"} filterValue={filterValue === null || isEmpty(filterValue?.destLocation) ? null : filterValue?.destLocation?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={handleFilterValueChange} customValue={itemCustomValue} treeload={treeLoad} enableMultiSelect selectLeafs/>
        <IconButton
          aria-describedby={"prodHrchyPop"}
          size="small"
          sx={{ mr: 1 }}
          onClick={filterActionItem}
          color={itemFilterColor}>
          <Tooltip title={t("itemFilter")}>
            <FilterAlt />
          </Tooltip>
        </IconButton>
        <AbcXyzFilterModal invClassCdList={invClassCdList} setInvClassCdList={handleInvClassCdListChange} />
        <ZeroExclusionFilter appliedExceptions={appliedExceptions} setAppliedExceptions={handleAppliedExceptionsChange} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"exceptionsOnly"} checked={onlyExceptionApplied} onChange={handleCheckbox} />}
          label={t("exceptionsOnly")} key="EXCEPTIONS_ONLY" sx={{ display: "flex" }}
        />
        <Box sx={{ ml: "auto" }}>
          <Tooltip title={editTarget === SIM_RP_ORDER ? t("editArrivalQty"): t("editRpOrder")}>
            <Box component={"span"}>
              <IconButton
                aria-label="toggleEditTarget"
                color={editTarget === SIM_ARRIVAL ? "info" : "default"}
                size="small"
                onClick={handleToggleEditTarget}
              >
                <SwapVert />
              </IconButton>
            </Box>
          </Tooltip>
          <EditButton label="rpConfiguration" size="medium" onClick={() => setPsiEditModalOpen(true)} disabled={isNoRowSelected} />
          <SettingsButton label="inventoryCoverageDaysSettings" size="medium" onClick={() => setThresholdModalOpen(true)} />
          <Tooltip title={t("layout")}>
            <IconButton
              size="small"
              color={layoutOpen || size(layoutModalData) > 2 ? muicolor.INFO : muicolor.DEFAULT}
              onClick={handleLayoutOpen}>
              <ViewComfySharp />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("notes")}>
            <IconButton
              size="small"
              color="info"
              onClick={handleNoteModal}>
              <EditNote />
            </IconButton>
          </Tooltip>
          <ExcelExportButton
            title={title}
            service={psiSimulationService}
            setExcelData={setGridDatas}
            columnDefs={[...defaultExportColumnDefs, ...bucketColumnDefs.current]}
            hasUnsavedChanges={!isEmpty(simulationUpdateDatas)}
            totalRows={resultTotalCount.current * rowsPerCombination}
            params={{ ...getRequestParams(false), measureCount: rowsPerCombination, measures: isEmpty(measures) ? isEmpty(p13Measures) ? allMeasure : p13Measures : measures, attributes: getAttributesParam()  }}
            isCsv
            translationColumns={["measure", "orderMethod", "orderCalendar"]}
            noteDefs={noteDefs}
          />
          <DeleteButton disabled={isEmpty(rpVersionFilter)} onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_VersionPermanentlyDelete", { version: rpVersionFilter?.name }) })} />
          <SaveButton disabled={isEmpty(rpVersionFilter)} onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <CheckButton label="confirm" disabled={isEmpty(rpVersionFilter)} onClick={() => setRunModalOpen(true)} />
          <FilterButton label="advancedFilter" advancedFilterColor={isAFActive ? muicolor.INFO : muicolor.DEFAULT} onClick={handleFilterOpen} />
        </Box>
      </FilterContainer>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <SplitterContainer>
            <Box id="psi-simulation-ag-grid" sx={{ height: "100%" }}>
              <Box className="ag-theme-balham grid-area non-background-selected-row" mb={1} sx={{ display: "flex", height: "calc(100% - 50px)" }}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={simulationData}
                    {...defaultGridMemo}
                    rowSelection={{
                      ...defaultGridMemo.rowSelection,
                      groupSelects: "descendants",
                      checkboxLocation: "autoGroupColumn"
                    }}
                    columnDefs={defaultColumnDefs}
                    groupDisplayType={'custom'}
                    groupDefaultExpanded={-1}
                    groupHideOpenParents
                    processDataFromClipboard={processDataFromClipboard}
                    onRowSelected={onRowSelected}
                    onCellClicked={onCellGridClicked}
                    onRowDataUpdated={initCheck}
                    onGridReady={onGridReady}
                    onCellValueChanged={onCellValueChanged}
                    onSortChanged={(params) => {
                      settingSortParams(params);
                      // 정렬은 재조회 없이 행 순서만 바뀌므로 노트 마커를 다시 계산한다
                      params.api.redrawRows();
                    }}
                    onColumnVisible={(params) => params.api.refreshCells({ force: true, columns: NOTE_MARKER_REFRESH_COLS })}
                    statusBar={statusBar}
                  >
                  </AgGridReact>
                </Box>
              </Box>
              <PaginationContainer
                currentPage={currentPage} totalPages={gridPages}
                pageSize={pageSize} pageSizes={pageSizes}
                setCurrentPage={setCurrentPage} setPageSize={setPageSize}
              />
            </Box>
            <Box id="psi-simulation-chart" className="chart-grid-area chart-area" sx={{ height: "100%" }}>
              <PivotChartComposed
                pivotConfig={pivotConfig}
                data={chartData}
                options={{ tooltipContentStyle: { fontSize: FS.SUB } }}
                nullConvert
                activeMeasures={!isEmpty(measures) ? measures : !isEmpty(p13Measures) ? p13Measures : allMeasure}
              />
            </Box>
          </SplitterContainer>
        </Box>
        {/*
          * 우측 패널 3종은 같은 줄에 나란히 선다 — 툴바 버튼 순서(사용자 정의 → 노트 → 고급 필터)와 같게.
          * 노트는 예전에 그리드 행 **안**에 있어서 페이지네이션·차트 위까지만 닿았다 — 옆에 선
          * 사용자 정의 패널만 화면 끝까지 내려와 둘의 높이가 어긋났다. 셋 다 이 행에 둔다.
          * 감싸는 Box 는 두지 않는다: 패널이 폭 조절 손잡이를 형제로 내놓기 때문에(SidePanel resizable)
          * 감싸면 손잡이가 세로로 떨어지고 폭 상한(부모 행의 70%)도 깨진다.
          */}
        <LayoutListModal
          open={layoutOpen}
          close={setLayoutOpen}
          pageCd={viewName}
          modalResultData={setLayoutModalData}
          displayNames={displayName}
          activeMeasure={(!isEmpty(measures) ? measures : !isEmpty(p13Measures) ? p13Measures : allMeasure)
            ?.map(mea => ({ measureCd: mea, name: mea, displayName: getMeasureDisplayName(mea) }))}
          reduxViewState={reduxViewState}
        />
        {!noteOpen ? (
          <NotesModal
            version={rpVersionFilter}
            timeDimension={rpVersionFilter?.planConfigObj?.BUCKET_UNIT ?? MONTH}
            cellData={noteCellData}
            pageCd={viewName}
            setUpdateCellData={setUpdateNoteData}
            updateFlg={setNoteUpdateFlg}
            close={handleNoteModal}
          />
        ) : null}
        {openFilter && (
          <>
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={advancedFilterColumnDefs}
                advancedFilters={pendingAdvancedFilters ?? advancedFilters}
                onApply={handleAdvancedFilterApply}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          </>
        )}
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <CalendarModal
        readOnly
        modalOpen={calendarModalOpen}
        setModalOpen={setCalendarModalOpen}
        selectedCycle={cycleCalendar}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        requiredMeasure={requiredMeasure}
        applyMeasures={applyMeasures}
        defaultMeasure={defaultMeasure}
        displayNames={displayName}
        hasDisplayName={true}
      />
      <RPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={item.name}
        modalResultData={setFilterData}
        filterTypes={{ Item: itemFilterType ?? [filterType.or.cd, filterType.equal.cd] }}
        filteredList={{ Item: itemFilterList ?? {} }}
      />
      <RpPsiSimulationEditModal
        open={psiEditModalOpen}
        setOpen={setPsiEditModalOpen}
        applyInvCovDays={setInvCovDaysConfig}
        viewName={viewName}
        rpPlanVersion={rpVersionFilter}
        selectedRow={selectedRow}
        retrievePsiSimulation={retrievePsiSimulation}
      />
      <InvCovDaysSettings
        open={thresholdModalOpen}
        setOpen={setThresholdModalOpen}
        applyInvCovDays={setInvCovDaysConfig}
        viewName={viewName}
      />
      <RunModal
        title={"confirm"}
        viewName={viewName}
        modalOpen={runModalOpen}
        setModalOpen={setRunModalOpen}
        defaultValue={defaultValue}
        setSelectedItemHrchy={setSelectItemHrchy}
        setIsRun={setIsRun}
        message={isEmpty(simulationUpdateDatas) ? t("MSG_RunSimulConfirm") : t("MSG_RunSimulConfirmAfterSaving")}
      />
      <DimensAttrModal
        open={dimenModalOpen}
        close={handleDimenClose}
        viewName={`${viewName}_ETC`}
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        selectLv={{}}
        attrConfig={attrConfig}
      />
    </>
  );
};

export default withTranslation()(PsiSimulation);
